import { createTheme, ThemeProvider } from '@mui/material';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import React from 'react';
import { Alignments, Borders, Colors, Margins, Mui, Percentages, Spacing } from '.';
import {
  BackgroundImage,
  Fullscreen,
  FullscreenView,
  MobileLoginBackgroundImage,
  DesktopLoginBackgroundImage,
} from './mui';
import { Svgs } from '../assets/svg';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

declare module '@mui/material/Paper' {
  // These customizations are for cards, but the card variants come from paper
  interface PaperPropsVariantOverrides {
    selected: true;
    disabled: true;
    ghost: true;
    innerCard: true;
    embeddedCard: true;
    shadow: true;
    contained: true;
  }
}

declare module '@mui/material/TableCell' {
  interface TableCellPropsVariantOverrides {
    hasButton: true;
    dataHead: true;
    dataBodyKey: true;
    dataBodyValue: true;
    dataBodyBold: true;
  }
}

declare module '@mui/material/TableBody' {
  interface TableBodyPropsVariantOverrides {
    deletableRows: true;
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    ghost: true;
    activeGhost: true;
    link: true;
    tertiary: true;
  }

  interface ButtonPropsColorOverrides {
    tertiary: true;
    accent: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    p10: true;
    p12: true;
    p12SemiBold: true;
    p12Bold: true;
    p13: true;
    p14: true;
    p14SemiBold: true;
    p14SemiBoldMontserrat: true;
    p14Bold: true;
    p16: true;
    p16Bold: true;
    p16SemiBold: true;
    p16Italic: true;
    p16Montserrat: true;
    p18: true;
    p18SemiBold: true;
    p18Bold: true;
    p18Italic: true;
    p20: true;
    p20Bold: true;
    p20SemiBold: true;
    p22Bold: true;
    p22BoldMontserrat: true;
    p24: true;
    p24Bold: true;
    p24SemiBold: true;
    p25Bold: true;
    p26: true;
    p26BoldMontserrat: true;
    p30: true;
    p30Bold: true;
    p32: true;
    p32Bold: true;
    p32BoldMontserrat: true;
    p32Light: true;
    p40: true;
    p40Bold: true;
    p45Bold: true;
    p50Light: true;
    p50Bold: true;
    p12OpenSansSemiBold: true;
    p14OpenSans: true;
    p16OpenSans: true;
    p18OpenSans: true;
    p18OpenSansBold: true;
    p20OpenSansSemiBold: true;
    p30OpenSansLight: true;
  }
}
declare module '@mui/material/styles' {
  // These are properties that we added inside the primary{}, secondary{}, etc. objects
  interface PaletteColor {
    main: string;
    active?: string;
    hover?: string;
    disabled?: string;
    dark: string;
  }
  interface SimplePaletteColorOptions {
    active?: string;
    hover?: string;
    disabled?: string;
    dark?: string;
    light?: string;
  }

  interface Palette {
    tertiary: Palette['primary'];
    accent: Palette['primary'];
    guidedChoice: Palette['primary'];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
    accent?: PaletteOptions['primary'];
    guidedChoice?: PaletteOptions['primary'];
  }

  interface TypographyVariants {
    p10: React.CSSProperties;
    p12: React.CSSProperties;
    p12SemiBold: React.CSSProperties;
    p12Bold: React.CSSProperties;
    p13: React.CSSProperties;
    p14: React.CSSProperties;
    p14SemiBold: React.CSSProperties;
    p14SemiBoldMontserrat: React.CSSProperties;
    p14Bold: React.CSSProperties;
    p16: React.CSSProperties;
    p16Bold: React.CSSProperties;
    p16SemiBold: React.CSSProperties;
    p16Italic: React.CSSProperties;
    p16Montserrat: React.CSSProperties;
    p18: React.CSSProperties;
    p18SemiBold: React.CSSProperties;
    p18Bold: React.CSSProperties;
    p18Italic: React.CSSProperties;
    p20: React.CSSProperties;
    p20Bold: React.CSSProperties;
    p20SemiBold: React.CSSProperties;
    p22Bold: React.CSSProperties;
    p22BoldMontserrat: React.CSSProperties;
    p24: React.CSSProperties;
    p24Bold: React.CSSProperties;
    p24SemiBold: React.CSSProperties;
    p25Bold: React.CSSProperties;
    p26: React.CSSProperties;
    p26BoldMontserrat: React.CSSProperties;
    p30: React.CSSProperties;
    p30Bold: React.CSSProperties;
    p32: React.CSSProperties;
    p32Light: React.CSSProperties;
    p40: React.CSSProperties;
    p40Bold: React.CSSProperties;
    p45Bold: React.CSSProperties;
    p50Light: React.CSSProperties;
    p50Bold: React.CSSProperties;
    p12OpenSansSemiBold: React.CSSProperties;
    p14OpenSans: React.CSSProperties;
    p16OpenSans: React.CSSProperties;
    p18OpenSans: React.CSSProperties;
    p18OpenSansBold: React.CSSProperties;
    p20OpenSansSemiBold: React.CSSProperties;
    p30OpenSansLight: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    p10?: React.CSSProperties;
    p12?: React.CSSProperties;
    p12SemiBold?: React.CSSProperties;
    p12Bold?: React.CSSProperties;
    p13?: React.CSSProperties;
    p14?: React.CSSProperties;
    p14SemiBold?: React.CSSProperties;
    p14SemiBoldMontserrat?: React.CSSProperties;
    p14Bold?: React.CSSProperties;
    p16?: React.CSSProperties;
    p16Bold?: React.CSSProperties;
    p16SemiBold?: React.CSSProperties;
    p16Italic?: React.CSSProperties;
    p16Montserrat: React.CSSProperties;
    p18: React.CSSProperties;
    p18SemiBold: React.CSSProperties;
    p18Bold?: React.CSSProperties;
    p18Italic?: React.CSSProperties;
    p20?: React.CSSProperties;
    p20Bold?: React.CSSProperties;
    p20SemiBold?: React.CSSProperties;
    p22Bold?: React.CSSProperties;
    p22BoldMontserrat?: React.CSSProperties;
    p24?: React.CSSProperties;
    p24Bold?: React.CSSProperties;
    p24SemiBold?: React.CSSProperties;
    p25Bold: React.CSSProperties;
    p26: React.CSSProperties;
    p26BoldMontserrat: React.CSSProperties;
    p30: React.CSSProperties;
    p30Bold: React.CSSProperties;
    p32: React.CSSProperties;
    p32Bold: React.CSSProperties;
    p32BoldMontserrat: React.CSSProperties;
    p32Light: React.CSSProperties;
    p40: React.CSSProperties;
    p40Bold: React.CSSProperties;
    p45Bold: React.CSSProperties;
    p50Light: React.CSSProperties;
    p50Bold: React.CSSProperties;
    p12OpenSansSemiBold?: React.CSSProperties;
    p14OpenSans?: React.CSSProperties;
    p16OpenSans?: React.CSSProperties;
    p18OpenSans?: React.CSSProperties;
    p18OpenSansBold?: React.CSSProperties;
    p20OpenSansSemiBold?: React.CSSProperties;
    p30OpenSansLight?: React.CSSProperties;
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: Colors.primaryBase,
      active: '#125163',
      hover: '#165f74',
      disabled: '#d4ebf2',
      light: Colors.primaryLight,
    },
    secondary: {
      main: Colors.secondaryBase,
      active: '#161d4b',
      hover: '#1e2257',
      disabled: '#b2b6cc',
      dark: '#022a3a',
    },
    info: { main: '#7a8e96' },
    error: { main: Colors.errorBase, light: '#f8dddb' },
    success: { main: '#90a15d', light: '#e9ecde', dark: Colors.successBase },
    tertiary: {
      main: '#4d9927',
      active: '#4f793a',
      hover: '#3f7e20',
      disabled: '#b8d6a9',
    },
    accent: {
      main: Colors.accentBase,
      active: Colors.accentDark,
      hover: '#874831',
      disabled: Colors.accentLighter,
    },
    guidedChoice: {
      main: Colors.cancelPolicy,
    },
  },
  typography: {
    fontFamily: 'Roboto, OpenSans, system-ui, -apple-system, sans-serif',
    h1: {
      fontSize: 30,
      fontWeight: 'bold',
    },
    h2: {
      fontSize: 18,
    },
    h3: {
      fontSize: 16,
      fontWeight: 600,
    },
    h4: {
      fontSize: 14,
      fontWeight: 600,
    },
    h5: {
      fontSize: 14,
      fontStyle: 'italic',
    },
    subtitle1: {
      fontSize: 30,
      fontWeight: 600,
    },
    body1: {
      fontSize: 16,
    },
    button: {
      fontSize: 18,
      fontWeight: 600,
    },
    // for custom variants, you need to re-specify the font family
    p10: {
      fontSize: 10,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p12: {
      fontSize: 12,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p12SemiBold: {
      fontSize: 12,
      fontWeight: 600,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p12Bold: {
      fontSize: 12,
      fontWeight: 'bold',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p13: {
      fontSize: 13,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p14: {
      fontSize: 14,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p14SemiBold: {
      fontSize: 14,
      fontWeight: 500,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p14SemiBoldMontserrat: {
      fontSize: 14,
      fontWeight: 600,
      fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
    },
    p14Bold: {
      fontSize: 14,
      fontWeight: 'bold',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p16: {
      fontSize: 16,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p16Bold: {
      fontSize: 16,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
      fontWeight: 'bold',
    },
    p16SemiBold: {
      fontSize: 16,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
      fontWeight: 600,
    },
    p16Italic: {
      fontSize: 16,
      fontStyle: 'italic',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p16Montserrat: {
      fontSize: 16,
      fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
      fontWeight: 'normal',
    },
    p18: {
      fontSize: 18,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p18SemiBold: {
      fontSize: 18,
      fontWeight: '600',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p18Bold: {
      fontSize: 18,
      fontWeight: 'bold',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p18Italic: {
      fontSize: 18,
      fontStyle: 'italic',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p20: {
      fontSize: 20,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p20Bold: {
      fontSize: 20,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
      fontWeight: 'bold',
    },
    p20SemiBold: {
      fontSize: 20,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
      fontWeight: 600,
    },
    p22Bold: {
      fontSize: 22,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
      fontWeight: 'bold',
    },
    p22BoldMontserrat: {
      fontSize: 22,
      fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
      fontWeight: 'bold',
    },
    p24: {
      fontSize: 24,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p24SemiBold: {
      fontSize: 24,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
      fontWeight: 600,
    },
    p24Bold: {
      fontSize: 24,
      fontWeight: 'bold',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p25Bold: {
      fontSize: 25,
      fontWeight: 'bold',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p26: {
      fontSize: 26,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p26BoldMontserrat: {
      fontSize: 26,
      fontWeight: 'bold',
      fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
    },
    p30: {
      fontSize: 30,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p30Bold: {
      fontSize: 30,
      fontWeight: 'bold',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p32: {
      fontSize: 32,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p32Bold: {
      fontSize: 32,
      fontWeight: 'bold',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p32BoldMontserrat: {
      fontSize: 32,
      fontWeight: 'bold',
      fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
    },
    p32Light: {
      fontSize: 32,
      fontWeight: 300,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p40: {
      fontSize: 40,
      fontWeight: 300,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p40Bold: {
      fontSize: 40,
      fontWeight: 'bold',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p45Bold: {
      fontSize: 45,
      fontWeight: 'bold',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p50Light: {
      fontSize: 50,
      fontWeight: 300,
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p50Bold: {
      fontSize: 50,
      fontWeight: 'bold',
      fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
    },
    p12OpenSansSemiBold: {
      fontSize: 12,
      fontWeight: 600,
      fontFamily: 'OpenSans, system-ui, -apple-system, sans-serif',
    },
    p14OpenSans: {
      fontSize: 14,
      fontFamily: 'OpenSans, system-ui, -apple-system, sans-serif',
    },
    p16OpenSans: {
      fontSize: 16,
      fontFamily: 'OpenSans, system-ui, -apple-system, sans-serif',
    },
    p18OpenSans: {
      fontSize: 18,
      fontFamily: 'OpenSans, system-ui, -apple-system, sans-serif',
    },
    p18OpenSansBold: {
      fontSize: 18,
      fontWeight: 'bold',
      fontFamily: 'OpenSans, system-ui, -apple-system, sans-serif',
    },
    p20OpenSansSemiBold: {
      fontSize: 20,
      fontWeight: 600,
      fontFamily: 'OpenSans, system-ui, -apple-system, sans-serif',
    },
    p30OpenSansLight: {
      fontSize: 30,
      fontWeight: 300,
      fontFamily: 'OpenSans, system-ui, -apple-system, sans-serif',
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          textTransform: 'none',
          wordWrap: 'break-word',
          lineHeight: 1.25,
          ...Alignments.verticalAlignCenter,
          ...(ownerState.color === 'accent' && {
            color: theme.palette.accent?.main,
          }),
          ...(ownerState.className === 'link-wrapper' && {
            display: 'inline',
          }),
          ...(ownerState.className === 'hex-header' && {
            ...Alignments.verticalAlignBottom,
            marginBottom: '5px',
          }),
          ...(ownerState.className === 'header-text' && {
            ...Alignments.verticalAlignBottom,
            marginBottom: '10px',
            marginRight: '20px',
          }),
          ...(ownerState.className === 'title' && {
            marginTop: '10px',
            fontSize: '30px',
          }),
          ...(ownerState.className === 'subtitle' && {
            marginTop: '16px',
          }),
          ...(ownerState.className === 'section-header' && {
            marginBottom: '10px',
          }),
          ...(ownerState.className === 'helper-text' && {
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: 1.66,
            marginTop: '4px',
          }),
          ...(ownerState.className === 'account-type-header' && {
            'marginBottom': '10px',
            'display': 'flex',
            'alignItems': 'center',
            'fontSize': '18px',
            'color': Colors.primaryBase,
            '& svg, & .svg-img': {
              marginRight: '10px',
            },
          }),
          ...(ownerState.className === 'clickable' && {
            'textDecoration': 'underline',
            '&:hover': {
              cursor: 'pointer',
            },
          }),
          ...(ownerState.className === 'hover-clickable' && {
            '&:hover': {
              cursor: 'pointer',
              textDecoration: 'underline',
            },
          }),
          ...(ownerState.className === 'no-underline-clickable' && {
            '&:hover': {
              cursor: 'pointer',
            },
          }),
          ...(ownerState.className === 'start-screen-text' && {
            marginTop: '30px',
          }),
          ...(ownerState.className === 'login-screen-text' && {
            marginTop: '30px',
            display: 'flex',
            justifyContent: 'center',
          }),
          ...(ownerState.className === 'bulleted-text' && {
            'color': 'green',
            'display': 'list-item',
            'listStyle': 'outside',
            'marginLeft': '40px',
            '&::marker': {
              content: '"•\t"',
            },
          }),
          ...(ownerState.className === 'one-line' && {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: '1',
            WebkitBoxOrient: 'vertical',
          }),
          ...(ownerState.className === 'two-lines' && {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: '2',
            WebkitBoxOrient: 'vertical',
          }),
          ...(ownerState.className === 'more-less' && {
            'display': 'block',
            'overflow': 'hidden',
            '& span': {
              fontWeight: 'bold',
              color: Colors.primaryBase,
              cursor: 'pointer',
            },
          }),
          ...(ownerState.className === 'lms-quiz-text' && {
            '& *': {
              fontFamily: 'inherit !important',
              fontSize: 'inherit !important',
              fontWeight: 'inherit !important',
              color: 'inherit !important',
            },
          }),
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          'borderRadius': '8px',
          ...(ownerState.color === 'primary' && {
            backgroundColor: Colors.tertiaryBase,
            border: `1px solid ${theme.palette.info.main}`,
            color: theme.palette.secondary.main,
          }),
          ...(ownerState.color === 'secondary' && {
            backgroundColor: '#dfeef4',
            border: 'none',
          }),
          ...(ownerState.color === 'accent' && {
            backgroundColor: Colors.accentBase,
            border: 'none',
          }),
          ...(ownerState.variant === 'elevation' && {
            border: `5px solid ${theme.palette.info.main}`,
          }),
          ...(ownerState.variant === 'selected' && {
            border: `1px solid ${Colors.tertiaryBase}`,
            backgroundColor: Colors.primaryBase,
            color: `${Colors.tertiaryBase} !important`,
          }),
          ...(ownerState.variant === 'ghost' &&
            ownerState.color === 'primary' && {
              border: 'none',
              backgroundColor: 'rgba(3, 138, 183, 0.05)',
              color: theme.palette.secondary.dark,
            }),
          ...(ownerState.variant === 'ghost' &&
            ownerState.color === 'secondary' && {
              border: 'none',
              backgroundColor: Colors.tertiaryBase,
              color: theme.palette.secondary.main,
            }),
          ...(ownerState.variant === 'innerCard' && {
            border: 'none',
            backgroundColor: Colors.primaryLightest,
            color: theme.palette.secondary.main,
          }),
          ...(ownerState.className === 'packageCard' && {
            border: `1px solid ${Colors.primaryLightest}`,
            boxShadow: 'none',
          }),
          ...(ownerState.className === 'learnMorePackageCard' && {
            border: 'none',
            boxShadow: 'none',
            backgroundColor: Colors.primaryLightest,
          }),
          ...(ownerState.variant === 'embeddedCard' && {
            border: 'none',
            backgroundColor: Colors.primaryLighter,
            color: theme.palette.secondary.main,
          }),
          ...(ownerState.className === 'content-card' && {
            position: 'relative',
            padding: '20px',
            boxShadow: 'none',
            border: `1px solid ${Colors.primaryLightest}`,
            display: 'flex',
            alignItems: 'center',
          }),
          ...(ownerState.className === 'content-title-card' && {
            'paddingLeft': '20px',
            'paddingRight': '20px',
            'boxShadow': 'none',
            'display': 'flex',
            'alignItems': 'center',
            'backgroundColor': 'transparent',
            '& > :not(:last-child)': {
              paddingRight: '10px',
            },
          }),

          '& .panel-summary-screen': {
            'boxShadow': 'none',
            'border': `1px solid ${Colors.primaryLightest}`,
            'borderRadius': '12px',
            '& a': {
              textDecoration: 'none',
            },
            '& .MuiIconButton-root': {
              padding: '0 !important',
            },
            '& .group-panel-options': {
              '& > div': {
                borderBottom: `1px solid ${Colors.primaryLight}`,
              },
              '& > div:last-child': {
                borderBottom: 'none',
              },
            },
            '&:hover:not(:has(.panel:hover))': {
              cursor: 'pointer',
              backgroundColor: Colors.disabledLight,
            },
          },
          ...(ownerState.className === 'quiz-results-card' && {
            padding: '10px 25px',
            boxShadow: 'none',
            border: `1px solid ${Colors.primaryLight}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }),
          ...(ownerState.className === 'projection-card' && {
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundColor': Colors.tertiaryBase,
            'boxShadow': 'none',
            'borderRadius': '12px',
            'padding': '16px 20px',
            'cursor': 'default',
            'flexBasis': '100%',
            'minWidth': 0,
            'display': 'flex',
            'flexDirection': 'column',
            'justifyContent': 'space-between',
            '& svg, & .svg-img': {
              flexShrink: 0,
            },
            '& .projection-card-header': {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '8px',
              rowGap: '16px',
              flexWrap: 'wrap',
            },
            '& .projection-value-group': {
              display: 'flex',
              columnGap: '32px',
              rowGap: '16px',
              marginBottom: '4px',
              flexWrap: 'wrap',
            },
            '& .projection-value': {
              marginBottom: '5px',
            },
            '& .projection-legend': {
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            },
            '& .projection-legend-dot': {
              width: 10,
              height: 10,
              borderRadius: '50%',
            },
            '& .projection-card-empty-content': {
              'display': 'flex',
              'flexDirection': 'column',
              'alignItems': 'center',
              'gap': '8px',
              'marginBottom': '18px',
              '& .MuiTypography-root': {
                textAlign: 'center',
              },
            },
          }),
          ...(ownerState.className === 'projection-section-empty' && {
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundColor': Colors.tertiaryBase,
            'boxShadow': 'none',
            'borderRadius': '12px',
            'padding': '20px 40px',
            'display': 'flex',
            'flexWrap': 'wrap',
            'alignItems': 'center',
            'justifyContent': 'center',
            'gap': '16px',
            '& svg, & .svg-img': {
              width: '100px',
              height: '100px',
              flexShrink: 0,
            },
            '& .projection-section-empty-content': {
              'flex': 1,
              'display': 'flex',
              'flexDirection': 'column',
              'alignItems': 'center',
              'gap': '8px',
              '& .MuiTypography-root': {
                textAlign: 'center',
              },
            },
          }),
          ...(ownerState.className === 'panel' && {
            '& a': {
              textDecoration: 'none',
            },
            '& .MuiTypography-root': {
              'cursor': 'pointer',
              '&:hover': {
                backgroundColor: Colors.disabledLight,
              },
            },
            '& .MuiIconButton-root': {
              padding: '0 !important',
            },
            '& .group-panel-options': {
              '& > .MuiGrid-root': {
                cursor: 'pointer',
              },
              '& > div': {
                borderBottom: `1px solid ${Colors.primaryLight}`,
              },
              '& > div:last-child': {
                borderBottom: 'none',
              },
            },
          }),
          ...(ownerState.className === 'rectangle-panel' && {
            'border': 'inherit',
            ...Borders.borderRadius_10,
            'backgroundColor': Colors.tertiaryBase,
            '& .button-container': {
              ...Alignments.alignSelfCenter,
            },
            '& .MuiCardHeader-action': {
              alignSelf: 'center',
            },
          }),
          ...(ownerState.className === 'rectangle-panel' &&
            ownerState.raised && {
              border: 'none',
            }),
          ...(ownerState.className === 'collapsible-panel-skeleton' && {
            borderRadius: '10px',
            marginBottom: '10px',
            border: `solid 1px ${Colors.primaryLightest}`,
            backgroundColor: Colors.tertiaryBase,
          }),
          ...(ownerState.className === 'collapsible-panel' && {
            'borderRadius': '0px',
            'marginBottom': '10px',
            'border':
              ownerState.variant === 'contained'
                ? `solid 1px ${Colors.primaryLightest}`
                : undefined,
            'boxShadow':
              ownerState.variant === 'shadow' ? '0 4px 7px 1px rgba(30, 41, 105, 0.2)' : 'none',
            'backgroundColor':
              ownerState.variant === 'shadow' || ownerState.variant === 'contained'
                ? undefined
                : 'transparent',
            ...(ownerState.variant === 'shadow' || ownerState.variant === 'contained'
              ? Borders.borderRadius_10
              : undefined),
            '& .MuiCardHeader-content': {
              overflow: 'hidden',
              minWidth: 0,
            },
            '& .collapsible-panel-header': {
              '&:hover': {
                'cursor': 'pointer',
                '& .icon': {
                  visibility: 'visible',
                },
              },
            },
            '& .collapsible-panel-title': {
              flexWrap: 'nowrap',
            },
            '& .MuiCardHeader-root': {
              ...Borders.borderRadius_10,
              'padding': '10px 20px',
              'backgroundColor': Colors.tertiaryBase,
              '& .icon': {
                visibility: 'hidden',
              },

              '& .MuiCardHeader-action': {
                alignSelf: 'center',
              },
            },
            '& .collapsible-panel:not(:last-of-type)': {
              borderBottom: `1px solid ${Colors.primaryLight}`,
              marginBottom: '0px',
            },
            '& .compressed-panel': {
              'borderRadius': '0px',
              'padding': '10px 20px',
              'backgroundColor': 'transparent',
              'border': 'none',
              '&:hover': {
                'cursor': 'pointer',
                '& .icon': {
                  visibility: 'visible',
                },
              },
            },
            '& .embedded-content': {
              paddingLeft: '60px',
            },
            '& .embedded-content .embedded-list': {
              paddingLeft: '6px',
            },
            '& .MuiCardContent-root': {
              padding: '0px',
            },
          }),
          ...(ownerState.className === 'card-display' && {
            '& .MuiCardHeader-root': {
              'backgroundColor': Colors.primaryLightest,
              'color': Colors.secondaryBase,
              'alignContent': 'center',
              '& .MuiCardHeader-action': {
                fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
              },
            },
            '& .card-action': {
              color: Colors.primaryBase,
            },
            'boxShadow':
              '0 4px 7px 1px rgba(30, 41, 105, 0.2), 0 -4px 7px 1px rgba(30, 41, 105, 0.2);',
            'backdropFilter': 'blur(6px)',
          }),
          ...(ownerState.className === 'mb-ranked-plan-card' && {
            'padding': '30px 20px',
            'width': '100%',
            'minWidth': '300px',
            '& .MuiCardHeader-root': {
              backgroundColor: Colors.tertiaryBase,
              alignContent: 'center',
              borderRadius: '10px',
            },
            'boxShadow':
              '0 4px 7px 1px rgba(30, 41, 105, 0.2), 0 -4px 7px 1px rgba(30, 41, 105, 0.2);',
            'backdropFilter': 'blur(6px)',
          }),
          ...(ownerState.className === 'account-card' && {
            'backgroundColor': Colors.tertiaryBase,
            'border': `1px solid ${Colors.primaryLightest}`,
            'width': Percentages.Full,
            '& .MuiCardHeader-root': {
              'paddingTop': '12px',
              'paddingBottom': '12px',
              'paddingLeft': '12px',
              'paddingRight': '40px',
              '& :last-child': {
                marginTop: '5px',
              },
            },
          }),
          ...(ownerState.className === 'empty-account-card' && {
            backgroundColor: Colors.tertiaryBase,
            marginTop: theme.spacing(3),
            height: 62,
            border: `1px solid ${Colors.primaryLightest}`,
            boxShadow: 'none',
            ...Alignments.alignCenter,
            ...Borders.borderRadius_7,
          }),
          ...(ownerState.className === 'empty-payoff-debt-card' && {
            backgroundColor: Colors.tertiaryBase,
            marginTop: '15px',
            boxShadow: 'none',
            maxWidth: '850px',
            width: '100%',
            padding: '23px 23px 23px 50px',
            borderRadius: '10px',
            backdropFilter: 'blur(9px)',
            border: `solid 1px ${Colors.primaryLight}`,
            ...Alignments.alignCenter,
            ...Borders.borderRadius_7,
          }),
          ...(ownerState.className === 'check-select' && {
            'backgroundColor': Colors.primaryLightest,
            'cursor': 'pointer',
            'minHeight': '51px',
            ...Margins.mt_sm,
            '& .MuiCardHeader-root': {
              padding: '6px',
              paddingLeft: '12px',
            },
            '&:hover': {
              backgroundColor: Colors.primaryLighter,
            },
          }),
          ...(ownerState.className === 'check-select-checked' && {
            'backgroundColor': Colors.primaryBase,
            'cursor': 'pointer',
            ...Margins.mt_sm,
            '& .MuiCardHeader-root': {
              padding: '6px',
              paddingLeft: '12px',
            },
            '& .MuiCardHeader-action': {
              fontSize: '30px',
            },
            '&:hover': {
              backgroundColor: theme.palette.primary.hover,
            },
          }),
          ...(ownerState.className === 'checkbox-card-checked' && {
            ...Margins.mt_sm,
            'backgroundColor': Colors.primaryLightest,
            'border': `1px solid ${Colors.primaryLight}`,
            'cursor': 'pointer',
            '& .MuiCardHeader-root': {
              padding: '0px',
            },
            '&:hover': {
              backgroundColor: Colors.primaryLighter,
            },
          }),
          ...(ownerState.className === 'checkbox-card-unchecked' && {
            ...Margins.mt_sm,
            'backgroundColor': Colors.tertiaryBase,
            'border': `1px solid ${Colors.primaryLightest}`,
            'cursor': 'pointer',
            '& .MuiCardHeader-root': {
              padding: '0px',
            },
            '&:hover': {
              backgroundColor: Colors.disabledLight,
            },
          }),
          ...(ownerState.className === 'investment-card' && {
            'width': Percentages.Full,
            '& .MuiCardHeader-root': {
              backgroundColor: Colors.primaryLightest,
              padding: '5px',
              paddingLeft: '35px',
            },
            '& .MuiCardHeader-action': {
              ...Alignments.alignSelfCenter,
            },
            '& .MuiSvgIcon-root': {
              fontSize: '30px',
            },
          }),
          ...(ownerState.className === 'security-details-card' && {
            'boxShadow': 'none',
            '& .MuiCardContent-root': {
              padding: '10px !important',
              backgroundColor: Colors.primaryLightest,
            },
          }),
          ...(ownerState.className &&
            ownerState.className.includes('institutional-account-card') && {
              'boxShadow':
                '0 4px 7px 1px rgba(30, 41, 105, 0.2), 0 -4px 7px 1px rgba(30, 41, 105, 0.2)',
              '&:hover, &.clicked': {
                '& .show-on-hover': { opacity: 1 },
              },
            }),
          ...(ownerState.className === 'dropdown-card' && {
            'backgroundColor': Colors.primaryLightest,
            'boxShadow': 'none',
            '& .card-action': {
              color: Colors.primaryBase,
            },
          }),
          ...(ownerState.className === 'leftover-card' && {
            backgroundColor: Colors.tertiaryBase,
            marginTop: theme.spacing(3),
            border: `1px solid ${Colors.primaryLightest}`,
            boxShadow: 'none',
            ...Borders.borderRadius_7,
          }),
          ...(ownerState.className &&
            ownerState.className.includes('goal-list-item') && {
              '&:hover, &.clicked': {
                '& .show-on-hover': { opacity: 1 },
                '& .hide-on-hover': { display: 'none' },
              },
            }),
          ...(ownerState.className === 'saved-guide' && {
            'backgroundColor': Colors.tertiaryBase,
            'border': `1px solid ${Colors.primaryLightest}`,
            'boxShadow': 'none',
            'padding': '10px 15px',
            'cursor': 'pointer',
            '&:hover': {
              backgroundColor: Colors.disabledLight,
            },
            ...Borders.borderRadius_7,
          }),
          ...(ownerState.className &&
            ownerState.className.includes('card-row') && {
              'border': `1px solid ${Colors.primaryLightest}`,
              'backdropFilter': 'blur(6px)',
              'boxShadow': 'none',
              '&:hover, &.clicked': {
                '& .show-on-hover': { opacity: 1 },
                '& .hide-on-hover': { display: 'none' },
              },
            }),
          ...(ownerState.className === 'panel-popover' && {
            minWidth: 'fit-content',
          }),
          ...(ownerState.className === 'plan-card' && {
            width: '335px',
            padding: '20px 20px 30px 20px',
            boxShadow: 'none',
            border: `1px solid ${Colors.primaryLightest}`,
            backdropFilter: 'blur(6px)',
          }),
          ...(ownerState.className === 'tools-carousel-card' && {
            'flexDirection': 'column',
            'display': 'flex',
            'padding': '10px',
            'boxShadow': 'none',
            'border': `1px solid ${Colors.primaryLighter}`,
            'alignItems': 'center',
            'justifyContent': 'center',
            'height': '135px',
            'width': '135px',
            '& .card-svg': {
              height: '75px',
              width: '75px',
            },
            '& img': {
              height: '60px',
              width: '60px',
            },
          }),
          ...(ownerState.className === 'advice-carousel-card' && {
            'flexDirection': 'column',
            'display': 'flex',
            'padding': '10px',
            'boxShadow': 'none',
            'backgroundColor': Colors.secondaryLighter,
            'border': `1px solid ${Colors.primaryLight}`,
            'alignItems': 'center',
            'height': '135px',
            'width': '135px',
            '& .card-svg': {
              height: '75px',
              width: '75px',
            },
            '& img': {
              height: '60px',
              width: '60px',
            },
          }),
          ...(ownerState.className === 'no-debts' && {
            padding: '10px 25px',
            boxShadow: 'none',
            borderRadius: '10px',
            border: `1px solid ${Colors.primaryLight}`,
            backdropFilter: 'blur(6px)',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            display: 'flex',
            minHeight: '250px',
          }),
          ...(ownerState.className === 'no-debts-how-much-debt' && {
            padding: '30px 40px',
            boxShadow: 'none',
            borderRadius: '10px',
            border: `1px solid ${Colors.primaryLight}`,
            backdropFilter: 'blur(6px)',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            display: 'flex',
            width: '100%',
          }),
          ...(ownerState.className === 'debt-ratio' && {
            padding: '35px',
            boxShadow: 'none',
            borderRadius: '12px',
            border: `1px solid ${Colors.primaryLightest}`,
            backdropFilter: 'blur(6px)',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            display: 'flex',
            flex: 1,
            minWidth: 0,
            maxWidth: '300px',
            height: '230px',
          }),
          ...(ownerState.className === 'chart-card' && {
            padding: '10px 0px',
            boxShadow: 'none',
            borderRadius: '10px',
            border: `1px solid ${Colors.primaryLight}`,
            backdropFilter: 'blur(6px)',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            display: 'flex',
          }),
          ...(ownerState.className === 'tools-output-box' && {
            maxHeight: '81px',
            minHeight: '81px',
            padding: '10px 0px',
            boxShadow: 'none',
            borderRadius: '10px',
            border: `1px solid ${Colors.primaryLight}`,
            backdropFilter: 'blur(6px)',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            textAlign: 'center',
            display: 'flex',
          }),
          ...(ownerState.className === 'tools-chart-output-box' && {
            padding: '10px 0px',
            boxShadow: 'none',
            borderRadius: '10px',
            border: `1px solid ${Colors.primaryLight}`,
            backdropFilter: 'blur(6px)',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            textAlign: 'center',
            display: 'flex',
          }),
          ...(ownerState.className === 'zillow-card' && {
            'position': 'relative',
            'padding': '10px',
            'boxShadow': 'none',
            'border': `1px solid ${Colors.primaryLightest}`,
            'display': 'flex',
            'alignItems': 'center',
            '&:hover': {
              cursor: 'pointer',
            },
          }),
          ...(ownerState.className === 'tools-no-items-added-box' && {
            padding: '20px 25px',
            boxShadow: 'none',
            borderRadius: '10px',
            border: `1px solid ${Colors.primaryLight}`,
            backdropFilter: 'blur(6px)',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '20px',
          }),
          ...(ownerState.className === 'most-popular-car-container' && {
            maxHeight: '100px',
            minHeight: '100px',
            padding: '10px 0px',
            boxShadow: 'none',
            borderRadius: '10px',
            border: `1px solid ${Colors.primaryLight}`,
            backdropFilter: 'blur(6px)',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            textAlign: 'center',
            display: 'flex',
          }),
          ...(ownerState.className === 'hoverable-info-card' && {
            'padding': '20px 15px',
            'borderRadius': '12px',
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundColor': Colors.tertiaryBase,
            'boxShadow': 'none',
            '&:hover': {
              backgroundColor: Colors.disabledLight,
              cursor: 'pointer',
            },
          }),
          ...(ownerState.className === 'income-card' && {
            position: 'relative',
            padding: '10px 40px',
            boxShadow: 'none',
            border: `1px solid ${Colors.primaryLightest}`,
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
          }),
          ...(ownerState.className === 'basic-investing-terms' && {
            width: '500px',
            padding: '10px',
            boxShadow: 'none',
            border: `1px solid ${Colors.primaryLightest}`,
          }),
          ...(ownerState.className === 'org-contact-info-card' && {
            'position': 'relative',
            'padding': '20px',
            'boxShadow': 'none',
            'border': `1px solid ${Colors.primaryLightest}`,
            'display': 'flex',
            'flexDirection': 'row',
            'alignItems': 'flex-start',
            'justifyContent': 'flex-start',
            '& .break-word': {
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              maxWidth: '100%',
            },
          }),
          ...(ownerState.className === 'covers-expenses-card' && {
            padding: '15px 20px',
            boxShadow: 'none',
            border: `1px solid ${Colors.primaryLightest}`,
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }),
          ...(ownerState.className === 'lms-content-card' && {
            'position': 'relative',
            'padding': '20px',
            'boxShadow': 'none',
            'border': `1px solid ${Colors.primaryLightest}`,
            'display': 'flex',
            'alignItems': 'center',
            'marginTop': '10px',
            'marginBottom': '15px',
            '& > div': {
              '& p': {
                padding: '5px',
              },
              '& ul, li': {
                marginLeft: '20px',
              },
            },
          }),
          ...(ownerState.className === 'income-replacement-card' && {
            'display': 'flex',
            'boxShadow': 'none',
            'padding': '20px',
            'justifyContent': 'space-between',
            'alignItems': 'center',
            'backgroundColor': Colors.tertiaryBase,
            'border': `1px solid ${Colors.primaryLight}`,
            'borderRadius': '12px',
            '& .left-side': {
              direction: 'row',
              display: 'flex',
              alignItems: 'center',
              columnGap: '20px',
            },
          }),
          ...(ownerState.className === 'income-replacement-option-card' && {
            'width': '135px',
            'height': '172px',
            'flexDirection': 'column',
            'display': 'flex',
            'alignItems': 'center',
            'padding': '20px 5px',
            'rowGap': '10px',
            'backdropFilter': 'blur(6px)',
            'boxShadow': '0 4px 7px 1px rgba(30, 41, 105, 0.2)',
            'backgroundColor': Colors.tertiaryBase,
            'borderRadius': '10px',
            '&:hover': {
              cursor: 'pointer',
            },
          }),
          ...(ownerState.className === 'custom-option-explainer-card' && {
            boxShadow: 'none',
            padding: '20px',
            backgroundColor: Colors.secondaryLighter,
            border: `1px solid ${Colors.primaryLight}`,
          }),
          ...(ownerState.className === 'income-replacement-option-card' && {
            'border': `2px solid ${ownerState.variant === 'selected' ? Colors.primaryLight : 'transparent'}`,
            'backgroundColor':
              ownerState.variant === 'selected' ? Colors.secondaryLightest : undefined,
            '&:hover': {
              cursor: 'pointer',
              backgroundColor:
                ownerState.variant === 'selected'
                  ? `${Colors.primaryLightest} !important`
                  : Colors.disabledLight,
            },
          }),
          ...(ownerState.className === 'migration-unavailable-subscription-card' && {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: 'none',
            borderRadius: '11.5px',
            border: `1px solid ${Colors.primaryLightest}`,
            backgroundColor: Colors.tertiaryBase,
            padding: '20px 50px',
            marginTop: '20px',
          }),
        }),
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.className === 'search-option' && {
            width: '100%',
            backgroundColor: Colors.primaryLightest,
            borderRadius: '7px',
          }),
          ...(ownerState.role === 'expanded' && {
            borderBottomLeftRadius: '0px !important',
            borderBottomRightRadius: '0px !important',
          }),
          ...(ownerState.className === 'status-card' && {
            'height': '100%',
            '& .show-on-hover': {
              visibility: 'hidden',
            },
            '&:hover': {
              '& .show-on-hover': {
                visibility: 'visible',
              },
            },
          }),
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.className === 'basic' && {
            padding: '16px !important',
          }),
          ...(ownerState.className === 'deletable' && {
            padding: '16px 50px 16px 16px',
            backgroundColor: Colors.primaryLightest,
          }),
          ...(ownerState.className === 'dataTable' && {
            padding: '16px 50px',
            backgroundColor: Colors.primaryLightest,
          }),
          ...(ownerState.className === 'basicCard' && {
            padding: '30px 30px 35px 50px',
          }),
          ...(ownerState.className === 'pad30' && {
            padding: '30px !important',
          }),
          ...(ownerState.className === 'innerGlance' && {
            padding: '20px 60px',
          }),
          ...(ownerState.className === 'communications' && {
            padding: '30px 50px',
          }),
          ...(ownerState.className === 'innerContent' && {
            margin: '0px 30px',
          }),
          ...(ownerState.className === 'helpChoose' && {
            margin: '0px 10px',
            textAlign: 'center',
            height: '50%',
          }),
          ...(ownerState.className === 'innerContent' && {
            padding: '30px 30px 30px 50px',
          }),
          ...(ownerState.className === 'pad20' && {
            padding: '20px !important',
          }),
          ...(ownerState.className === 'centered-inner' && {
            padding: '16px !important',
            alignItems: 'center',
          }),
          ...(ownerState.className === 'reviewCost' && {
            padding: '30px',
            textAlign: 'center',
          }),
          ...(ownerState.className === 'productCard' && {
            padding: '50px !important',
          }),
          ...(ownerState.className === 'yearlyPrice' && {
            padding: '20px 30px',
          }),
          ...(ownerState.className === 'whoPays' && {
            padding: '10px 20px !important',
          }),
          ...(ownerState.className === 'packageCard' && {
            padding: '20px',
            paddingBottom: '20px !important',
          }),
          ...(ownerState.className === 'learnMorePackageCard' && {
            padding: '20px',
            paddingBottom: '20px !important',
          }),
          ...(ownerState.className === 'packageCardContents' && {
            padding: '20px',
            paddingBottom: '20px !important',
          }),
          ...(ownerState.className === 'learnMorePackageCardContents' && {
            padding: '20px',
            paddingBottom: '20px !important',
          }),
          ...(ownerState.className === 'payment' && {
            '& .row': {
              justifyContent: 'space-between',
            },
          }),
          ...(ownerState.className === 'institution' && {
            '& .row': {
              justifyContent: 'space-between',
            },
          }),
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          'borderRadius': '10px',
          'backdropFilter': 'blur(6px)',
          'boxShadow':
            '0 4px 7px 1px rgba(30, 41, 105, 0.2), 0 -4px 7px 1px rgba(30, 41, 105, 0.2)',
          'marginTop': '30px',
          'display': 'flex',
          'flexDirection': 'column',
          'justifyContent': 'center',
          '& .MuiButtonBase-root': {
            borderRadius: '7px',
            margin: '10px 16px',
            fontSize: '14px',
          },
          '& .no-options': {
            padding: '14px 16px',
            display: 'flex',
            justifyContent: 'center',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          textTransform: 'none',
          fontSize: 14,
          fontWeight: 'semiBold',
          fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
          lineHeight: '2em',
          padding: '5px 35px',
          borderRadius: '27.5px',
          whiteSpace: 'nowrap',
          minWidth: 'fit-content',
          ...(ownerState.color === 'primary' && {
            'backgroundColor': theme.palette.primary.main,
            'color': Colors.tertiaryBase,
            '&:hover': {
              backgroundColor: theme.palette.primary.hover,
            },
            '&:active': {
              backgroundColor: theme.palette.primary.active,
            },
            '&:disabled': {
              backgroundColor: theme.palette.primary.disabled,
              color: Colors.tertiaryBase,
            },
          }),
          ...(ownerState.color === 'secondary' && {
            'backgroundColor': theme.palette.secondary.main,
            'color': Colors.tertiaryBase,
            '&:hover': {
              backgroundColor: theme.palette.secondary.hover,
            },
            '&:active': {
              backgroundColor: theme.palette.secondary.active,
            },
            '&:disabled': {
              backgroundColor: theme.palette.secondary.disabled,
            },
          }),
          ...(ownerState.color === 'tertiary' && {
            'backgroundColor': theme.palette.tertiary?.main,
            'color': Colors.tertiaryBase,
            '&:hover': {
              backgroundColor: theme.palette.tertiary?.hover,
            },
            '&:active': {
              backgroundColor: theme.palette.tertiary?.active,
            },
            '&:disabled': {
              backgroundColor: theme.palette.tertiary?.disabled,
            },
          }),
          ...(ownerState.color === 'accent' && {
            'backgroundColor': theme.palette.accent?.main,
            'color': Colors.tertiaryBase,
            '&:hover': {
              backgroundColor: theme.palette.accent?.hover,
            },
            '&:active': {
              backgroundColor: theme.palette.accent?.active,
            },
            '&:disabled': {
              backgroundColor: theme.palette.accent?.disabled,
            },
          }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'primary' && {
              'backgroundColor': Colors.tertiaryBase,
              'color': theme.palette.primary.main,
              '&:hover': {
                backgroundColor: '#e2e6e7',
              },
              '&:active': {
                backgroundColor: '#d9d9d9',
                color: theme.palette.primary.active,
              },
            }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'secondary' && {
              'backgroundColor': Colors.tertiaryBase,
              'color': theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: '#e2e6e7',
              },
              '&:active': {
                backgroundColor: '#d9d9d9',
                color: theme.palette.secondary.active,
              },
            }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'tertiary' && {
              'backgroundColor': Colors.tertiaryBase,
              'color': theme.palette.tertiary?.main,
              '&:hover': {
                backgroundColor: '#e2e6e7',
              },
              '&:active': {
                backgroundColor: '#d9d9d9',
                color: theme.palette.tertiary?.active,
              },
            }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'accent' && {
              'backgroundColor': Colors.tertiaryBase,
              'color': theme.palette.accent?.main,
              '&:hover': {
                backgroundColor: '#e2e6e7',
              },
              '&:active': {
                backgroundColor: '#d9d9d9',
                color: theme.palette.accent?.active,
              },
            }),
          ...(ownerState.variant === 'activeGhost' && {
            'border': 'none',
            'backgroundColor': Colors.tertiaryBase,
            'color': theme.palette.secondary.main,
            '&:hover': {
              backgroundColor: '#f2f2f2',
            },
            '&:active': {
              backgroundColor: '#d9d9d9',
            },
          }),
          ...(ownerState.variant === 'ghost' && {
            'border': 'none',
            'backgroundColor': Colors.tertiaryBase,
            'color': theme.palette.primary.main,
            '&:hover': {
              backgroundColor: '#f2f2f2',
            },
            '&:active': {
              backgroundColor: '#d9d9d9',
            },
          }),
          ...(ownerState.variant === 'ghost' &&
            ownerState.color === 'primary' && {
              'border': 'none',
              'backgroundColor': 'transparent',
              'color': theme.palette.primary?.main,
              '&:hover': {
                backgroundColor: '#f2f2f2',
              },
              '&:active': {
                backgroundColor: '#d9d9d9',
              },
            }),
          ...(ownerState.variant === 'ghost' &&
            ownerState.color === 'secondary' && {
              'border': 'none',
              'backgroundColor': 'transparent',
              'color': theme.palette.secondary?.main,
              '&:hover': {
                backgroundColor: '#f2f2f2',
              },
              '&:active': {
                backgroundColor: '#d9d9d9',
              },
            }),
          ...(ownerState.variant === 'ghost' &&
            ownerState.color === 'accent' && {
              'border': 'none',
              'backgroundColor': 'transparent',
              'color': theme.palette.accent?.main,
              '&:hover': {
                backgroundColor: '#f2f2f2',
              },
              '&:active': {
                backgroundColor: '#d9d9d9',
              },
            }),
          ...(ownerState.className === 'ghostAlt' && {
            'border': 'none',
            'backgroundColor': 'transparent',
            'fontFamily': 'Montserrat, system-ui, -apple-system, sans-serif',
            'fontSize': 14,
            'fontWeight': 600,
            'color': theme.palette.primary.main,
            '&:hover': {
              textDecoration: 'underline',
              backgroundColor: 'transparent',
            },
            '&:active': {
              backgroundColor: 'transparent',
            },
          }),
          ...(ownerState.variant === 'link' && {
            'border': 'none',
            'backgroundColor': Colors.tertiaryBase,
            'color': theme.palette.primary.main,
            'textDecoration': 'underline',
            '&:hover': {
              backgroundColor: '#f2f2f2',
            },
            '&:active': {
              backgroundColor: '#d9d9d9',
            },
          }),
          ...(ownerState.variant === 'link' &&
            ownerState.color === 'secondary' && {
              'border': 'none',
              'backgroundColor': 'transparent',
              'color': theme.palette.secondary?.main,
              'textDecoration': 'underline',
              '&:hover': {
                backgroundColor: '#f2f2f2',
              },
              '&:active': {
                backgroundColor: '#d9d9d9',
              },
            }),
          ...(ownerState.variant === 'link' &&
            ownerState.color === 'accent' && {
              'border': 'none',
              'backgroundColor': 'transparent',
              'color': theme.palette.accent?.main,
              'textDecoration': 'underline',
              '&:hover': {
                backgroundColor: '#f2f2f2',
              },
              '&:active': {
                backgroundColor: '#d9d9d9',
              },
            }),
          ...(ownerState.className === 'black' && {
            'border': 'none',
            'backgroundColor': 'black',
            'color': Colors.tertiaryBase,
            '&:hover': {
              backgroundColor: '#f2f2f2',
            },
            '&:active': {
              backgroundColor: '#d9d9d9',
            },
          }),
          ...(ownerState.className === 'font16' && {
            'border': 'none',
            'backgroundColor': Colors.tertiaryBase,
            'color': theme.palette.secondary.main,
            'fontSize': 16,
            '&:hover': {
              backgroundColor: '#f2f2f2',
            },
            '&:active': {
              backgroundColor: '#d9d9d9',
            },
          }),
          ...(ownerState.className === 'learnMore' && {
            'border': 'none',
            'backgroundColor': Colors.tertiaryBase,
            'color': theme.palette.primary.main,
            'textDecorationLine': 'underline',
            '&:hover': {
              backgroundColor: Colors.tertiaryBase,
            },
            '&:active': {
              backgroundColor: Colors.tertiaryBase,
            },
          }),
          ...(ownerState.className === 'activeLearnMore' && {
            'border': 'none',
            'backgroundColor': Colors.primaryBase,
            'color': Colors.tertiaryBase,
            'textDecorationLine': 'underline',
            '&:hover': {
              backgroundColor: Colors.primaryBase,
            },
            '&:active': {
              backgroundColor: Colors.primaryBase,
            },
          }),
          ...(ownerState.className === 'link' && {
            'border': 'none',
            'color': Colors.primaryBase,
            'fontSize': '12px',
            'backgroundColor': 'transparent',
            'textDecorationLine': 'underline',
            '&:hover': {
              backgroundColor: 'trasparent',
            },
            '&:active': {
              backgroundColor: 'trasparent',
            },
          }),
          ...(ownerState.className === 'gcCancel' && {
            'fontSize': 14,
            'fontFamily': 'OpenSans, system-ui, -apple-system, sans-serif',
            'backgroundColor': Colors.tertiaryBase,
            'color': theme.palette.guidedChoice?.main,
            'border': '1px solid',
            'borderColor': theme.palette.guidedChoice?.main,
            'borderRadius': '4px',
            'padding': '5px 15px',
            'width': '11%',
            '&:hover': {
              backgroundColor: '#e2e6e7',
            },
            '&:active': {
              backgroundColor: '#d9d9d9',
              color: theme.palette.secondary.dark,
            },
          }),
          ...(ownerState.className?.includes('glow-button') && {
            'position': 'relative',
            'padding': '10px 20px',
            'borderRadius': '12px',
            'transition': 'all 0.5s ease',

            '@keyframes glow': {
              '0%': {
                filter: `
                  drop-shadow(0 0 0px #00eaff)
                  drop-shadow(0 0 2px #00a2ca)
                  drop-shadow(0 0 4px #12637c)
                `,
              },
              '100%': {
                filter: `
                  drop-shadow(0 0 4px #00eaff)
                  drop-shadow(0 0 6px #00a2ca)
                  drop-shadow(0 0 8px #12637c)
                `,
              },
            },

            '@keyframes trace': {
              to: {
                strokeDashoffset: '0',
              },
            },

            '&:hover': {
              'backgroundColor': Colors.primaryBase,
              'color': 'white',
              '& .border-rect': {
                fill: 'rgba(28, 66, 83, 0.1)',
                opacity: 0.9,
              },
            },

            '& .border-svg': {
              'position': 'absolute',
              'inset': '-2px',
              'pointerEvents': 'none',
              'animation': 'glow 800ms ease-out infinite alternate',
              'width': 'calc(100% + 4px)',
              'height': 'calc(100% + 4px)',

              '& .border-rect': {
                strokeDasharray: '400',
                strokeDashoffset: '400',
                animation: 'trace 4s ease-out forwards',
                stroke: '#fff',
              },
            },
          }),
          ...(ownerState.className?.includes('start-lesson-button') && {
            display: 'flex',
            color: 'white',
            height: '50%',
            alignSelf: 'center',
            fontSize: '30px',
            columnGap: '10px',
            fontWeight: 600,
            fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
            padding: '25px',
          }),
          ...(ownerState.className?.includes('walk-through-button') && {
            display: 'flex',
            color: 'white',
            alignSelf: 'center',
            padding: '12px',
          }),
          /* Some of these are unused and can change as we need them ; I'm making some guesses to what sort of widths we will want */
          ...(ownerState.className === 'xs' && {
            width: '10%',
          }),
          ...(ownerState.className === 'sm' && {
            width: '15%',
          }),
          ...(ownerState.className === 'md' && {
            width: '20%',
          }),
          ...(ownerState.className === 'lg' && {
            width: '25%',
          }),
          ...(ownerState.className === 'xl' && {
            width: '40%',
          }),
          ...(ownerState.className === 'xxl' && {
            width: '50%',
          }),
          ...(ownerState.className === 'xxxl' && {
            width: '100%',
          }),
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '& .MuiOutlinedInput-root': {
            'background': Colors.tertiaryBase,
            '& fieldset': {
              borderColor: `${theme.palette.primary.main}`,
            },
            '&:hover fieldset': {
              borderColor: `${theme.palette.primary.main}`,
            },
          },
          ...(ownerState.className === 'ghost-input' && {
            '& fieldset': {
              borderWidth: '0px',
            },
            '&:hover fieldset': {
              borderWidth: '1px',
            },
          }),
          ...(ownerState.className === 'flat' && {
            '& .MuiOutlinedInput-root fieldset': {
              border: 'none',
            },
            '& .MuiOutlinedInput-root.Mui-focused fieldset': {
              border: '2px solid',
              borderColor: theme.palette.primary.main,
            },
            '& .MuiOutlinedInput-root.Mui-error fieldset': {
              border: '1px solid',
              borderColor: theme.palette.error.main,
            },
            '& .MuiOutlinedInput-root.Mui-error.Mui-focused fieldset': {
              border: '2px solid',
              borderColor: theme.palette.error.main,
            },
          }),
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: () => ({
          'color': theme.palette.secondary.main,
          '&.Mui-readOnly': {
            'backgroundColor': Colors.disabledLight,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: Colors.disabledDark,
            },
            '&:hover fieldset': {
              borderColor: Colors.disabledDarker,
            },
          },
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: () => ({
          '&.Mui-checked': {
            color: theme.palette.primary,
          },
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          'display': 'inline',
          '&:hover': {
            cursor: 'pointer',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          backgroundColor: 'rgba(3, 138, 183, 0)',
          boxShadow: 'none',
          ...(ownerState.className === 'drawer-details' && {
            'width': '78%',
            '& .MuiTableCell-root': {
              textAlign: 'left',
            },
            '& .bold': {
              fontWeight: 'bold',
            },
          }),
          ...(ownerState.className === 'spending-overview-categories-container' && {
            'alignItems': 'baseline',
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundColor': Colors.tertiaryBase,
            'maxHeight': '50vh',
            'paddingLeft': Spacing.xl,
            'paddingRight': Spacing.xl,
            ...Borders.borderRadius_10,
            '& > .spending-overview-categories': {
              'display': 'flex',
              'flexDirection': 'column',
              '& .spending-overview-view': {
                'width': '100%',
                'paddingTop': '10px',
                'paddingBottom': '10px',
                '& .category-row': {
                  'display': 'flex',
                  'justifyContent': 'space-between',
                  'width': '100%',
                  'paddingTop': '10px',
                  'paddingBottom': '10px',
                  '& .category-section': {
                    display: 'flex',
                    width: '50%',
                    columnGap: '24px',
                    alignItems: 'center',
                  },
                  '& .amount-section': {
                    'display': 'flex',
                    'width': '50%',
                    '& > *': {
                      fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      width: '50%',
                    },
                  },
                  '& .circle': {
                    height: '20px',
                    transition: 'height 0.3s',
                  },
                  '&:hover': {
                    'cursor': 'pointer',
                    'paddingTop': '10px',
                    'paddingBottom': '10px',
                    '& .circle': {
                      height: '24px',
                    },
                    '& .category': {
                      textDecoration: 'underline',
                    },
                  },
                },
                '& > :not(:last-child)': {
                  borderBottom: `solid 1px ${Colors.primaryLighter}`,
                },
              },
            },
          }),
          ...(ownerState.className === 'search-results-container' && {
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundColor': Colors.tertiaryBase,
            'maxHeight': '50vh',
            'paddingLeft': Spacing.xl,
            'paddingRight': Spacing.xl,
            ...Borders.borderRadius_10,
            '& > .search-results-table': {
              'display': 'flex',
              'flexDirection': 'column',
              '& .search-results-table-body': {
                'width': '100%',
                'paddingTop': '10px',
                'paddingBottom': '10px',
                '& .search-results-row': {
                  'display': 'flex',
                  'justifyContent': 'space-between',
                  'width': '100%',
                  'paddingTop': '10px',
                  'paddingBottom': '10px',
                  '& .description-section': {
                    'display': 'flex',
                    'width': '70%',
                    'columnGap': '24px',
                    'alignItems': 'center',
                    'overflow': 'hidden',
                    '& .date': { width: '15%' },
                    '& .description': {
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '60%',
                    },
                    '& .church-name': {
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '90%',
                    },
                  },
                  '& .amount-section': {
                    'display': 'flex',
                    'justifyContent': 'space-between',
                    'width': '40%',
                    '& .category': {
                      display: 'flex',
                      justifyContent: 'flex-start',
                      width: '100%',
                    },
                    '& .amount': {
                      display: 'flex',
                      justifyContent: 'flex-end',
                      width: '100%',
                    },
                  },
                  '& .charity-results': {
                    'display': 'flex',
                    'width': '100%',
                    'columnGap': '24px',
                    'alignItems': 'center',
                    'overflow': 'hidden',
                    '& .charity-name': {
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '55%',
                    },
                    '& .link': {
                      '&:hover': {
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      },
                    },
                    '& .category': {
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '22.5%',
                    },
                    '& .location': {
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '22.5%',
                    },
                  },
                },
                '& > :not(:last-child)': {
                  borderBottom: `solid 1px ${Colors.primaryLighter}`,
                },
              },
            },
          }),
          ...(ownerState.className === 'transactions-search-results-container' && {
            'border': `solid 1px ${Colors.primaryLightest}`,
            'borderSpacing': 0,
            'backgroundColor': Colors.tertiaryBase,
            'maxHeight': '65vh',
            'paddingHorizontal': '10px',
            ...Borders.borderRadius_10,
            '& > .search-results-table': {
              'display': 'flex',
              'flexDirection': 'column',
              'padding': '10px 0px',
              '& .search-results-table-body': {
                'width': '100%',
                '& .search-results-row': {
                  'display': 'grid',
                  'justifyContent': 'space-between',
                  'gridColumns': 12,
                  '& .description-section': {
                    'display': 'grid',
                    'columnGap': '24px',
                    'alignItems': 'center',
                    'overflow': 'hidden',
                    '& .date': {},
                    '& .description': {
                      display: 'inline-grid',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    },
                    '& .church-name': {
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '90%',
                    },
                  },
                  '& .amount-section': {
                    'display': 'grid',
                    'justifyContent': 'space-between',
                    '& .category': {
                      display: 'grid',
                      justifyContent: 'flex-start',
                    },
                    '& .amount': {
                      display: 'grid',
                      justifyContent: 'flex-end',
                    },
                  },
                  '& .charity-results': {
                    'display': 'flex',
                    'width': '100%',
                    'columnGap': '24px',
                    'alignItems': 'center',
                    'overflow': 'hidden',
                    '& .charity-name': {
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '55%',
                    },
                    '& .link': {
                      '&:hover': {
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      },
                    },
                    '& .category': {
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '22.5%',
                    },
                    '& .location': {
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '22.5%',
                    },
                  },
                },
                '& > :not(:last-child)': {
                  borderBottom: `solid 1px ${Colors.primaryLighter}`,
                  borderSpacing: 0,
                },
              },
            },
          }),
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: () => ({
          color: theme.palette.secondary.dark,
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          padding: '16px 0px',
          ...(ownerState.variant === 'hasButton' && {
            ':last-of-type': {
              textAlign: 'right',
              borderBottom: 'none',
              fontSize: 16,
            },
          }),

          ...(ownerState.variant === 'head' && {
            borderBottom: `1px solid ${theme.palette.secondary.main}`,
            fontSize: 18,
            fontWeight: 600,
            color: theme.palette.primary.main,
          }),
          ...(ownerState.variant === 'body' && {
            borderBottom: 'none',
            fontSize: 18,
            color: theme.palette.secondary.main,
          }),
          ...(ownerState.variant === 'dataHead' && {
            border: 'none',
            fontSize: 18,
            fontWeight: 700,
            color: theme.palette.secondary.main,
            padding: '5px 10px',
            textAlign: 'right',
          }),
          ...(ownerState.variant === 'dataBodyKey' && {
            border: 'none',
            fontSize: 16,
            fontWeight: 700,
            color: theme.palette.primary.main,
            padding: '5px 10px',
            textAlign: 'right',
          }),
          ...(ownerState.variant === 'dataBodyValue' && {
            border: 'none',
            fontSize: 16,
            color: theme.palette.secondary.main,
            padding: '5px 10px',
          }),
          ...(ownerState.variant === 'dataBodyBold' && {
            border: 'none',
            fontSize: 18,
            fontWeight: 'bold',
            color: theme.palette.secondary.main,
          }),
        }),
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: <Svgs.DisplaySmallCustomCheckmarkFilled width={24} height={24} />,
          error: <Svgs.DisplaySmallCustomXFilled width={24} height={24} />,
          warning: <ErrorOutlineIcon fontSize='large' />,
        },
      },
      styleOverrides: {},
    },
    MuiCollapse: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          zIndex: 9999,
          ...(ownerState.className === 'toast' && {
            'position': 'absolute',
            'top': 0,
            'left': 0,
            'width': '100%',
            '& .MuiAlert-root': {
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              padding: '20px',
              justifyContent: 'center',
              backgroundColor: 'inherit',
            },
            '& .MuiAlert-icon': {
              fontSize: 40,
            },
            '& .MuiAlert-message': {
              marginLeft: '10px',
              marginRight: '48px',
              textAlign: 'center',
            },
            '& .MuiAlert-action': {
              position: 'absolute',
              right: '18px',
            },
            ...(ownerState.role === 'error' && {
              'backgroundColor': `${theme.palette.error.light}`,
              '& .MuiAlert-icon, & .MuiAlert-action, & .MuiAlert-root': {
                color: `${theme.palette.error.main} !important`,
              },
            }),
            ...(ownerState.role === 'success' && {
              'backgroundColor': `${theme.palette.success.light}`,
              '& .MuiAlert-icon, & .MuiAlert-action, & .MuiAlert-root': {
                color: `${theme.palette.success.dark} !important`,
              },
              '& .MuiAlert-icon': {
                fontSize: 16,
              },
            }),
            ...(ownerState.role === 'warning' && {
              'backgroundColor': Colors.alternateLighter,
              '& .MuiAlert-icon, & .MuiAlert-action, & .MuiAlert-root': {
                color: `${theme.palette.error.main} !important`,
              },
            }),
          }),
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.anchor === 'right' && {
            '& .MuiDrawer-paper': { width: Mui.DrawerWidth, minWidth: '500px' },
          }),
          ...(ownerState.className === 'action-sheet' && {
            '& .MuiDrawer-paper': { borderRadius: '8px 8px 0 0' },
          }),
          ...(ownerState.className === 'mobile-drawer' && {
            'boxSizing': 'border-box',
            '& .MuiDrawer-paper': { width: '100%', display: 'block', padding: '20px' },
          }),
          ...(ownerState.className === 'browser-drawer' && {
            '& .MuiDrawer-paper': {
              width: '60%',
              padding: '5px',
              minWidth: '500px',
              maxWidth: '850px',
            },
          }),
          ...(ownerState.role === 'past-invoice' && {
            '& .MuiBox-root': { maxWidth: '900px', padding: '20px', minWidth: '500px' },
          }),
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: Colors.tertiaryBase,
          zIndex: theme.zIndex.drawer + 1,
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.className === 'top-nav' && {
            display: 'flex',
            flex: 1,
            zIndex: 3,
            position: 'fixed',
            backgroundColor: Colors.tertiaryBase,
            color: Colors.cancelPolicy,
            height: '64px',
          }),
          ...(ownerState.className === 'app-header' && {
            boxShadow: '0 2px 2px 0 rgba(182, 192, 196, 0.3)',
          }),
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.className === 'budget-add-an-item-menu' && {
            '& .MuiPaper-root': {
              border: `1px solid ${Colors.primaryLightest}`,
              borderRadius: '10px',
              marginTop: '15px',
              maxHeight: '400px',
              minWidth: '400px',
              width: '400px',
              maxWidth: 'calc(100vw - 32px)',
              marginLeft: 0,
              overflow: 'hidden',
              backgroundColor: Colors.tertiaryBase,
              position: 'relative',
              padding: 0,
              filter: 'none',
            },
            '& .MuiPaper-root:before': {
              display: 'none',
            },
            '& .MuiList-root': {
              paddingTop: 0,
              paddingBottom: 0,
              maxHeight: '400px',
              overflowY: 'auto',
              overscrollBehaviorY: 'contain',
            },
            '& .MuiMenuItem-root:hover': {
              backgroundColor: Colors.disabledLight,
            },
            '& .MuiMenuItem-root.Mui-selected': {
              backgroundColor: 'transparent',
            },
            '& .MuiMenuItem-root.Mui-selected:hover': {
              backgroundColor: Colors.disabledLight,
            },
            '& .category-select-header': {
              padding: '10px',
              position: 'sticky',
              top: 0,
              zIndex: 4,
              backgroundColor: Colors.tertiaryBase,
            },
            '& .category-select-search': {
              padding: '0 10px',
              position: 'sticky',
              top: '40px',
              marginBottom: '5px',
              zIndex: 4,
              backgroundColor: Colors.tertiaryBase,
              minHeight: 'unset',
              lineHeight: 'normal',
            },
            '& .add-an-item-search .MuiOutlinedInput-root': {
              'background': Colors.primaryLightest,
              'borderRadius': '4px',
              'padding': '0 10px 0 7px ',
              '& .MuiOutlinedInput-input': {
                fontSize: '16px',
                padding: '5px 0px',
              },
              '& .MuiOutlinedInput-input::placeholder': {
                fontSize: '16px',
              },
              '& fieldset': {
                border: '1px solid transparent',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
                borderWidth: '1px',
              },
              '&:hover': {
                background: Colors.primaryLighter,
              },
              '&:hover fieldset': {
                borderColor: 'transparent',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '&.Mui-focused fieldset': {
                borderColor: Colors.primaryBase,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: Colors.primaryBase,
                borderWidth: '2px',
              },
            },
            '& .add-an-item-search.is-active .MuiOutlinedInput-root fieldset': {
              borderColor: Colors.primaryBase,
            },
            '& .add-an-item-search.is-active .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
              {
                borderColor: Colors.primaryBase,
                borderWidth: '2px',
              },
            '& .add-an-item-search.is-active .MuiOutlinedInput-root:hover fieldset': {
              borderColor: Colors.primaryBase,
            },
            '& .add-an-item-search.is-active .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
              {
                borderColor: Colors.primaryBase,
                borderWidth: '2px',
              },
            '& .category-select-section-header': {
              'padding': '5px 10px',
              'backgroundColor': Colors.tertiaryBase,
              'lineHeight': 'normal',
              'cursor': 'default',
              '&:hover': {
                backgroundColor: Colors.tertiaryBase,
                cursor: 'default',
              },
            },
            '& .category-select-item': {
              padding: '5px 0',
              paddingTop: '8px',
              paddingBottom: '8px',
              marginLeft: '10px',
              marginRight: '10px',
              borderRadius: '4px',
              paddingLeft: '40px',
            },
            '& .category-select-footer': {
              padding: '0 10px 10px',
              position: 'sticky',
              bottom: 0,
              zIndex: 4,
              backgroundColor: Colors.tertiaryBase,
            },
            '& .category-select-footer-button': {
              'textTransform': 'none',
              'borderRadius': '8px',
              'padding': '7px 10px',
              'backgroundColor': Colors.secondaryLightest,
              'border': `1px solid ${Colors.primaryLightest}`,
              'color': Colors.primaryBase,
              'display': 'flex',
              'justifyContent': 'space-between',
              'alignItems': 'center',
              'fontSize': '14px',
              'fontWeight': 700,
              '&:hover': {
                backgroundColor: Colors.primaryLighter,
              },
            },
            '& .category-no-results-card': {
              margin: '8px 10px',
              border: `1px solid ${Colors.primaryLightest}`,
              borderRadius: '8px',
              backgroundColor: Colors.tertiaryBase,
              padding: '20px 15px',
            },
          }),
          ...(ownerState.className?.includes('custom-expense-modal-menu') && {
            '& .MuiPaper-root': {
              border: `1px solid ${Colors.primaryLightest}`,
              borderRadius: '10px',
              backgroundColor: Colors.tertiaryBase,
              maxHeight: '210px',
              overflow: 'hidden',
              padding: 0,
            },
            '& .MuiList-root, & .MuiMenu-list': {
              backgroundColor: Colors.tertiaryBase,
              maxHeight: '210px',
              overflowY: 'auto',
              overscrollBehaviorY: 'contain',
              paddingTop: 0,
              paddingBottom: 0,
            },
            '& .MuiMenuItem-root': {
              padding: '5px 0',
              marginLeft: '10px',
              marginRight: '10px',
              borderRadius: '4px',
            },
            '& .MuiMenuItem-root:hover': {
              backgroundColor: Colors.disabledLight,
            },
          }),
        }),
        paper: ({ theme }) => ({
          'overflow': 'visible',
          'filter': 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          'mt': 1.5,
          '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          '& .MuiMenuItem-root': {
            '&:hover': {
              bgcolor: theme.palette.primary.light,
            },
            'color': theme.palette.secondary.main,
          },
          '& .placeholder': {
            display: 'none',
          },
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        }),
      },
    },
    MuiList: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.role === 'menu' &&
            ownerState.className === 'side-navigation' && {
              '& a': {
                textDecoration: 'unset !important',
                color: 'inherit !important',
              },
              '& .MuiDivider-root': {
                borderColor: `${theme.palette.secondary.main}`,
                marginRight: '12px',
                marginLeft: '12px',
                marginTop: '12px !important',
                marginBottom: '18px !important',
              },
              '& .MuiMenuItem-root': {
                marginTop: '10px',
                marginLeft: '10px !important',
              },
              '& .selected': {
                color: `${theme.palette.secondary.main} !important`,
                fontWeight: 'bold',
                borderRadius: '10px !important',
                backgroundColor: `${Colors.secondaryLightest} !important`,
                marginLeft: '15px !important',
                marginRight: '12px',
                height: '40px',
                justifyContent: 'left',
                paddingLeft: '12px !important',
              },
            }),
        }),
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          '& .MuiFormControlLabel-root': {
            'border': `1px solid ${
              ownerState.className === 'radio-error'
                ? theme.palette.error.main
                : theme.palette.primary.main
            }`,
            'padding': '10px 30px',
            'flex': '1',
            'width': '100%',
            'textAlign': 'center',
            'marginRight': 0,
            'marginLeft': 0,
            'justifyContent': 'center',
            'backgroundColor': Colors.tertiaryBase,
            'fontSize': '18px',
            '&:first-of-type': {
              borderTopLeftRadius: '4px',
              borderBottomLeftRadius: '4px',
            },
            '&:last-child': {
              borderTopRightRadius: '4px',
              borderBottomRightRadius: '4px',
            },
            '&.Mui-disabled': {
              cursor: 'not-allowed',
              borderColor: Colors.disabledBase,
            },
            '&.paleLabelContainerRest': {
              'border': 'none',
              '&:nth-of-type(odd)': {
                borderRight: `2px solid ${Colors.primaryLighter}`,
              },
              'color': `${Colors.secondaryBase}`,
              '& .MuiFormControlLabel-label': {
                fontWeight: 'normal !important',
              },
            },
            '&.paleLabelContainerChecked': {
              'border': 'none',
              'borderRadius': '4px',
              'backgroundColor': Colors.primaryLighter,
              'color': `${Colors.secondaryBase}`,
              'fontWeight': 600,
              '&.Mui-disabled': {
                backgroundColor: Colors.disabledBase,
                borderColor: Colors.disabledBase,
              },
            },
            '&.paleLabelContainerUnchecked': {
              'border': 'none',
              'borderRadius': '4px',
              'color': theme.palette.secondary.main,
              '& .MuiFormControlLabel-label': {
                fontWeight: 'normal !important',
              },
            },
            '&.labelContainerChecked': {
              'backgroundColor': theme.palette.primary.main,
              'color': theme.palette.primary.contrastText,
              '&.Mui-disabled': {
                backgroundColor: Colors.disabledBase,
                borderColor: Colors.disabledBase,
              },
            },
            '&.labelContainerUnchecked': {
              color: theme.palette.secondary.main,
            },
            '&.radio-error': {
              border: `1px solid ${theme.palette.error.main}`,
            },
            '& .MuiRadio-root': {
              display: 'none',
            },
          },
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.error && {
            '& .MuiInputBase-root': {
              borderColor: theme.palette.error.main,
            },
          }),
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          fontSize: 14,
          ...(ownerState.color === 'primary' && {
            color: theme.palette.primary.main,
          }),
          ...(ownerState.color === 'secondary' && {
            color: theme.palette.secondary.main,
          }),
          ...(ownerState.color === 'tertiary' && {
            color: theme.palette.tertiary?.main,
          }),
        }),
        label: {
          fontSize: 16,
          fontWeight: 600,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: () => ({
          color: Colors.errorBase,
          marginLeft: 0,
        }),
      },
    },
    MuiModal: {
      styleOverrides: {
        root: () => ({
          /* fill in if we need */
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: () => ({
          '&.ai-doc-upload-modal': {
            '& .MuiDialog-paper': {
              width: '100%',
              maxWidth: 'min(620px, calc(100% - 64px))',
            },
            '& .MuiPaper-root': {
              minWidth: '470px',
            },
            '& .MuiPaper-root.file-upload-dropzone': {
              minWidth: '300px',
            },
          },
          '&.custom-expense-modal': {
            '& .MuiPaper-root': {
              minWidth: '500px',
            },
            '& .MuiDialogContent-root': {
              padding: '15px !important',
              margin: '0px !important',
            },
            '& .MuiDialogActions-root': {
              padding: '15px !important',
              margin: '0px !important',
            },
            '& .browser-dialog': {
              margin: '0px !important',
              padding: '15px !important',
            },
            '& .MuiDialog-container .MuiPaper-root > .MuiIconButton-root': {
              display: 'none !important',
            },
            '& .MuiOutlinedInput-root': {
              backgroundColor: Colors.primaryLightest,
              borderRadius: '4px',
            },
            '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: Colors.primaryLightest,
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: Colors.primaryLightest,
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              fontSize: '16px',
              color: Colors.secondaryLight,
              opacity: 1,
            },
            '& .MuiSelect-select': {
              fontSize: '16px',
              padding: '8.5px 14px',
            },
            '& .select-placeholder': {
              color: Colors.secondaryLight,
            },
          },
          '&.page-content-with-background': {
            '& .MuiPaper-root': {
              ...FullscreenView,
              ...BackgroundImage,
            },
          },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.className === 'institutional-modal-content' && {
            'width': '600px',
            'display': 'flex',
            'flexDirection': 'column',
            'alignItems': 'center',
            'margin': '20px auto 12px auto !important',
            '& svg, & .svg-img': {
              width: '90px',
            },
            '& span': {
              textAlign: 'center',
            },
          }),
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.className === 'institutional-modal-actions' && {
            'display': 'flex',
            'flexDirection': 'column',
            'alignItems': 'center',
            'marginBottom': '10px',
            '& button': {
              height: '40px',
              width: '35%',
            },
          }),
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.color === 'primary' && {
            'color': theme.palette.primary.main,
            'backgroundColor': 'transparent',
            'borderColor': theme.palette.primary.main,
            '&::before': {
              borderColor: theme.palette.primary.main,
            },
            '&::after': {
              borderColor: theme.palette.primary.main,
            },
          }),
          ...(ownerState.color === 'primary.light' && {
            'color': theme.palette.primary.light,
            'backgroundColor': 'transparent',
            'borderColor': theme.palette.primary.light,
            '&::before': {
              borderColor: theme.palette.primary.light,
            },
            '&::after': {
              borderColor: theme.palette.primary.light,
            },
          }),
          ...(ownerState.color === 'secondary' && {
            'color': theme.palette.secondary.main,
            'backgroundColor': 'transparent',
            'borderColor': theme.palette.secondary.main,
            '&::before': {
              borderColor: theme.palette.secondary.main,
            },
            '&::after': {
              borderColor: theme.palette.secondary.main,
            },
          }),
          ...(ownerState.color === 'tertiary' && {
            'color': theme.palette.tertiary?.main,
            'backgroundColor': 'transparent',
            'borderColor': theme.palette.tertiary?.main,
            '&::before': {
              borderColor: theme.palette.tertiary?.main,
            },
            '&::after': {
              borderColor: theme.palette.tertiary?.main,
            },
          }),
          ...(ownerState.className === 'dependent-divider' && {
            marginTop: '16px',
            marginBottom: '16px',
          }),
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.className === 'date-picker-input-label' && {
            fontSize: '14px !important',
          }),
        }),
      },
    },
    MuiSelect: {
      variants: [
        {
          props: { variant: 'filled' },
          style: {
            borderRadius: '4px',
            border: `solid 1px ${Colors.primaryBase}`,
            backgroundColor: `${Colors.tertiaryBase} !important`,
            padding: '0 !important',
            input: {
              height: 'inherit !important',
              padding: '10px !important',
            },
            color: Colors.secondaryBase,
          },
        },
      ],
      styleOverrides: {
        select: ({ ownerState }) => ({
          paddingTop: '5px !important',
          paddingBottom: '5px !important',
          ...(ownerState.className === 'placeholder' && {
            color: 'green',
          }),
        }),
        icon: {
          fill: Colors.primaryBase,
        },
        root: ({ ownerState }) => ({
          ...(ownerState.className === 'flat' && {
            border: 'none !important',
          }),
        }),
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: () => ({
          '&.Mui-readOnly': {
            backgroundColor: `${Colors.disabledLight} !important`,
            border: `solid 1px ${Colors.disabledDark} !important`,
          },
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          'color': Colors.cancelPolicy,
          '&$checked': {
            color: '#038ab7 !important',
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '& > svg': {
            '& .hover-clickable': {
              cursor: 'pointer',
            },
          },
          ...(ownerState.className === 'content-wrapper' && {
            '& > *': {},
            ...FullscreenView,
            ...BackgroundImage,
            'display': 'grid',
            'gridTemplateRows': '97px 1fr min-content',
            'gridTemplateColumns': `${ownerState.id === 'expanded' ? '250px' : '85px'} auto`,
            'gridTemplateAreas': `"header header"
                                "sidebar content"
                                "sidebar footer"`,
          }),
          ...(ownerState.className?.includes('content-view') && {
            'maxHeight': '100%',
            '--content-max': '1200px',
            '& .content': {
              'display': 'flex',
              'flexDirection': 'row',
              'justifyContent': 'center',
              '& > .children': {
                maxWidth: 'var(--content-max)',
                width: '100%',
              },
            },
            '&.focused-content': {
              'alignSelf': 'start',
              'gridColumn': '1 / -1',
              'display': 'grid',
              'columnGap': '20px',
              'gridTemplateColumns': '1fr minmax(0, var(--content-max)) 1fr',
              'alignItems': 'start',
              'justifyContent': 'flex-start',
              'verticalAlign': 'top',
              '& > .focused-left': {
                gridColumn: '1',
                justifySelf: 'center',
                alignSelf: 'start',
                minWidth: '180px',
                overflow: 'hidden',
              },
              '& > .content': {
                alignSelf: 'start',
                gridColumn: '2',
                width: '100%',
              },
              // Prevent focused forms from looking too narrow
              '& .form-content .MuiGrid-grid-sm-6, & .form-content .MuiGrid2-grid-sm-6': {
                flex: '0 1 600px',
                maxWidth: '100%',
                minWidth: 0,
              },
            },
            '& > .tools-start-view-background': {
              position: 'absolute',
              bottom: 0,
              width: 'calc(100% - 250px)',
              opacity: '30%',
            },
            '& > .advice-focused-background': {
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: '90px',
              width: '100%',
              objectFit: 'cover',
              pointerEvents: 'none',
            },
          }),
          ...(ownerState.className === 'footer-content' && {
            '& .content': {
              'display': 'flex',
              'flexDirection': 'row',
              'justifyContent': 'center',
              '& > .children': {
                maxWidth: '1200px',
                paddingLeft: '40px',
                paddingRight: '40px',
                width: '100%',
              },
            },
            '& > .tools-start-view-background': {
              position: 'absolute',
              bottom: 0,
              width: 'calc(100% - 250px)',
              opacity: '30%',
            },
          }),
          ...(ownerState.className === 'page-content-with-background' && {
            ...FullscreenView,
            ...BackgroundImage,
          }),
          ...(ownerState.className === 'mobile-login-with-background' && {
            ...FullscreenView,
            ...MobileLoginBackgroundImage,
          }),
          ...(ownerState.className === 'desktop-login-with-background' && {
            ...FullscreenView,
            ...DesktopLoginBackgroundImage,
          }),
          ...(ownerState.className === 'page-content' && {
            ...Fullscreen,
          }),
          ...(ownerState.className === 'form-content' && {
            width: Percentages.Full,
          }),
          ...(ownerState.className === 'lms-side-nav' && {
            'backgroundColor': `${Colors.tertiaryBase} !important`,
            'color': `${Colors.primaryBase} !important`,
            'height': '100%',
            'boxShadow': '1px -1px 3px 1px rgba(30, 41, 105, 0.2)',
            'zIndex': 2,
            'display': 'flex',
            'flexDirection': 'column',
            'justifyContent': 'space-between',
            'position': 'relative',
            '& .lms-side-nav-content': {
              'padding': '20px',
              'paddingBottom': '0px',
              'display': 'flex',
              'flexDirection': 'column',
              'rowGap': '10px',
              'backgroundColor': '#fff',
              'height': 'fit-content',
              'maxHeight': 'calc(100vh - 188px)',
              '& .lms-side-nav-header': {
                'border': `solid 1px ${Colors.primaryLighter}`,
                'borderRadius': '10px',
                'padding': '10px',
                'alignItems': 'center',
                'display': 'flex',
                'columnGap': '10px',
                '&:hover': {
                  backgroundColor: `${Colors.disabledLight}`,
                  cursor: 'pointer',
                },
              },
              '& .MuiTableContainer-root': {
                maxHeight: '100%',
              },
              '& .MuiCardContent-root': {
                paddingBottom: '0px !important',
              },
              '& .collapsible-panel': {
                'marginBottom': '0px',
                '&:not(:last-child)': {
                  borderBottom: `solid 1px ${Colors.primaryLightest}`,
                },
              },
              '& .unit-progress-card-lesson': {
                'width': '100%',
                'borderRadius': '0px',
                'border': 'none',
                '&:first-child': {
                  borderTop: `solid 1px ${Colors.primaryLightest}`,
                },
              },
            },
          }),
          ...(ownerState.className === 'side-nav' && {
            'backgroundColor': `${Colors.tertiaryBase} !important`,
            'color': `${Colors.primaryBase} !important`,
            'height': '100%',
            'borderTopRightRadius': '20px',
            'boxShadow': '1px -1px 3px 1px rgba(30, 41, 105, 0.2)',
            'zIndex': 2,
            '& .side-toggle': {
              display: 'flex',
              justifyContent: 'center',
              visibility: 'hidden',
            },
            '&:hover': {
              '& .side-toggle': {
                visibility: 'visible',
              },
            },
          }),
          ...(ownerState.className === 'side-nav' &&
            ownerState.id === 'expanded' && {
              'width': 'inherit',
              '& .side-toggle': {
                display: 'flex',
                justifyContent: 'end',
                visibility: 'hidden',
              },
            }),
          ...(ownerState.className === 'outlined-card' && {
            border: `solid 1px ${Colors.primaryLight}`,
            boxShadow: 'none',
            padding: '10px',
            borderRadius: '10px',
          }),
          ...(ownerState.className === 'mailing-info' && {
            'backgroundColor': Colors.primaryLightest,
            ...Borders.borderRadius_7,
            '& .MuiSvgIcon-root': {
              marginTop: '11px',
              color: '#e5aa2b',
            },
          }),
          ...(ownerState.className === 'account-view-container' && {
            '& .account-view ~ .account-view': {
              marginTop: '30px',
            },
            '& .account-list': {
              '& > *': { justifyContent: 'start' },
            },
          }),
          ...(ownerState.className === 'group-edit-item' && {
            border: `solid 1px ${Colors.primaryLightest}`,
            backgroundColor: Colors.tertiaryBase,
            maxWidth: '1200px !important',
            flexBasis: '100% !important',
            ...Borders.borderRadius_10,
            ...Alignments.verticalAlignCenter,
          }),
          ...(ownerState.className === 'group-edit-item-needs-attention' && {
            border: `solid 1px ${Colors.errorBase}`,
            backgroundColor: Colors.tertiaryBase,
            maxWidth: '1200px !important',
            flexBasis: '100% !important',
            ...Borders.borderRadius_10,
            ...Alignments.verticalAlignCenter,
          }),
          ...(ownerState.className === 'group-edit-count' && {
            maxWidth: '1200px !important',
            flexBasis: '100% !important',
            ...Alignments.verticalAlignCenter,
          }),
          ...(ownerState.className === 'dependent-item' && {
            backgroundColor: Colors.primaryLightest,
            ...Borders.borderRadius_10,
            ...Alignments.verticalAlignCenter,
          }),
          ...(ownerState.className === 'dependent-count' && {
            ...Borders.borderRadius_10,
            ...Alignments.verticalAlignCenter,
          }),
          ...(ownerState.className === 'help' && {
            borderColor: `${Colors.primaryLightest} !important`,
            ...Alignments.verticalAlignCenter,
          }),
          ...(ownerState.className === 'account-list' && {
            '& .group-edit-item': {
              width: '100%',
              backgroundColor: Colors.tertiaryBase,
              ...Borders.borderRadius_10,
              ...Alignments.verticalAlignCenter,
            },
          }),
          ...(ownerState.className === 'dependent-select' && {
            '& > .add-dependent': {
              padding: '10px !important',
              paddingRight: '0px !important',
            },
          }),
          ...(ownerState.className === 'add-investment' && {
            'backgroundColor': Colors.primaryLightest,
            'borderRadius': 'inherit',
            '& .show-on-hover': {
              opacity: 0,
              visibility: 'hidden',
            },
            '&:hover': {
              '& .show-on-hover': {
                opacity: 1,
                visibility: 'visible',
              },
            },
          }),
          ...(ownerState.className === 'investments-giving-row' && {
            'backgroundColor': Colors.secondaryLightest,
            'borderRadius': '10px',
            'padding': '10px',
            'boxShadow': 'none',
            '& .show-on-hover': {
              opacity: 0,
              visibility: 'hidden',
            },
            '&:hover': {
              'backgroundColor': Colors.primaryLightest,
              '& .show-on-hover': {
                opacity: 1,
                visibility: 'visible',
              },
            },
          }),
          ...(ownerState.className === 'add-investment-tax-benefits' && {
            '& .show-on-hover': {
              opacity: 0,
              visibility: 'hidden',
            },
            '&:hover': {
              '& .show-on-hover': {
                opacity: 1,
                visibility: 'visible',
              },
            },
          }),
          ...(ownerState.className === 'duplicate-account-content' && {
            'marginTop': '30px',
            'display': 'flex',
            'flexDirection': 'column',
            'alignItems': 'center',
            '& .duplicate-account-info': {
              marginTop: '50px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            },
          }),
          ...(ownerState.className === 'help-text' && {
            '& > .MuiTypography-root': {
              textDecoration: 'underline',
              marginRight: '5px',
            },
          }),
          ...(ownerState.className === 'collapsible-investment-selection' && {
            '& > .MuiGrid-root': {
              width: '100%',
            },
            '& .group-panels': {
              'flexDirection': 'row',
              'justifyContent': 'center',
              'width': '100%',
              '@media (max-width: 975px)': {
                flexDirection: 'column',
              },
              '& > .panel-popover': {
                'boxShadow': 'none',
                '& > .MuiPaper-root': {
                  minWidth: '175px',
                },
              },
            },
            '& .MuiTypography-root': {
              color: `${Colors.secondaryBase}`,
              fontSize: '16px',
            },
          }),
          ...(ownerState.className === 'college-giving-goal-panels' && {
            '& .panel-popover': {
              flexGrow: 1,
              maxWidth: 'none',
            },
            ...Alignments.alignCenter,
          }),
          ...(ownerState.className === 'lms-course-panels' && {
            '& .panel-popover': {
              flexGrow: 1,
              maxWidth: '100%',
              minWidth: 0,
            },
            '& .group-panel-container': {
              width: '100%',
            },
            ...Alignments.alignCenter,
          }),
          ...(ownerState.className === 'where-next-panels' && {
            '& .panel-popover': {
              flexGrow: 1,
              maxWidth: '100%',
              minWidth: 0,
            },
            '& .group-panel-container': {
              display: 'flex',
              flexDirection: 'column',
              rowGap: `${Spacing.sm}px !important`,
            },
            '& .group-panels': {
              'display': 'grid',
              'columnGap': '20px',
              'rowGap': '23px',
              'gridTemplateColumns': 'repeat(3, 1fr)',
              '@media (max-width: 1200px)': {
                gridTemplateColumns: 'repeat(2, 1fr)',
              },
              '@media (max-width: 900px)': {
                gridTemplateColumns: '1fr',
              },
            },
            '& .MuiCard-root': {
              'height': '131px',
              'width': '239px',
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'borderRadius': '11.5px',
              'backdropFilter': 'blur(6px)',
              'boxShadow': '0 4px 7px 1px rgba(30, 41, 105, 0.2)',
              '& > .MuiGrid-root': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
            },
            '& .MuiTypography-p18Bold': {
              fontSize: '16px !important',
              fontWeight: '500 !important',
            },
            ...Alignments.alignCenter,
          }),
          ...(ownerState.className === 'manual-account-panels' && {
            '& .panel-popover': {
              flexGrow: 1,
              maxWidth: '100%',
              minWidth: 0,
            },
            '& .group-panel-container': {
              display: 'flex',
              flexDirection: 'column',
            },
            '& .MuiCard-root': {
              height: '160px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            ...Alignments.alignCenter,
          }),
          ...(ownerState.className === 'advice-section' && {
            '& .panel-popover.upsell-summary-panel': {
              '& .MuiTypography-root': {
                color: Colors.disabledDark,
              },
              '& .panel-content-container': {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
                columnGap: 1,
                minWidth: 0,
              },
              '& .panel-button-container': {
                flexShrink: 0.3,
                minWidth: 0,
              },
            },
          }),
          ...(ownerState.className === 'plaid-status-card' && {
            '& .rectangle-panel h3.MuiTypography-h6': {
              ...theme.typography.p18Bold,
            },
          }),
          ...(ownerState.className === 'account-view-status-icon' && {
            '& svg, & .svg-img': {
              height: '20px',
            },
          }),
          ...(ownerState.className === 'auto-save-indicator' && {
            'alignItems': 'center',
            '& > *': {
              marginRight: '10px !important',
            },
            '& .MuiCircularProgress-root, & svg, & .svg-img': {
              maxWidth: '20px',
              maxHeight: '20px',
            },
          }),
          ...(ownerState.className === 'embedded-list' && {
            'marginTop': '10px',
            'marginBottom': '16px',
            '& .embedded-list-header': {
              'width': '100%',
              '&:not(:hover)': {
                '& .show-on-hover': {
                  visibility: 'hidden',
                },
              },
              '& :hover': {
                'cursor': 'pointer',
                '& .show-on-hover': {
                  visibility: 'visible',
                },
              },
              '& :last-child': {
                flexGrow: 1,
              },
            },
            '& .embedded-list-content': {
              'paddingLeft': '0px',
              'borderColor': Colors.primaryLighter,
              'backgroundColor': Colors.secondaryLightest,
              '& .embedded-list-content-title-container': {
                minWidth: 0,
              },
              '& .embedded-list-content-title': {
                maxWidth: '100%',
                minWidth: 0,
              },
              '& .embedded-list-content-summary-container': {
                columnGap: '10px',
              },
              '& .embedded-list-content-summary-icon': {
                'display': 'flex',
                'alignItems': 'center',
                'justifyContent': 'center',
                'width': '14px',
                'minWidth': '14px',
                '& svg, & .svg-img': {
                  marginRight: '0 !important',
                },
              },
              '& .embedded-list-content-summary': {
                width: 'clamp(100px, 13vw, 130px)',
                minWidth: 'clamp(100px, 13vw, 130px)',
                textAlign: 'right',
              },
              '& > .MuiGrid-root': {
                padding: '0px',
              },
              '& > .MuiIconButton-root': {
                marginLeft: '2px',
                marginRight: '2px',
              },
              // '& :hover': {
              //   cursor: 'pointer',
              // },
              '&:not(:hover)': {
                '& .MuiIconButton-root': {
                  visibility: 'hidden',
                },
                '& .show-on-hover': {
                  visibility: 'hidden',
                },
              },
              [theme.breakpoints.down('sm')]: {
                '& .embedded-list-content-summary-container': {
                  columnGap: '10px',
                },
                '& .embedded-list-content-summary-icon': {
                  width: '12px',
                  minWidth: '12px',
                },
              },
            },
          }),
          ...(ownerState.className === 'embedded-form' && {
            '& .embedded-form-input:not(:first-child) .embedded-form-input__content': {
              borderTop: `solid 1px ${Colors.primaryLight}`,
            },
          }),
          ...(ownerState.className === 'embedded-form-input' && {
            'justifyContent': 'flex-start',
            '&:not(:hover, :focus-within)': {
              '& .MuiOutlinedInput-root': {
                visibility: 'hidden',
              },
              '& .MuiInputBase-input': {
                textAlign: 'end',
                paddingRight: '0px',
                visibility: 'visible',
                fontWeight: 'bold',
              },
            },
            '&:hover, &:focus-within': {
              cursor: 'pointer',
            },
            '& .embedded-form-input__rail': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '60px',
              flex: '0 0 60px',
              marginLeft: '-60px',
              paddingLeft: '25px',
            },
            '& .embedded-form-input__content': {
              paddingBottom: '3px',
              marginLeft: '6px',
              paddingTop: '3px',
              paddingRight: '20px',
            },
            '& .embedded-form-input__row': {
              columnGap: '8px',
              minWidth: 0,
            },
            '& .embedded-form-input__labels': {
              flexGrow: 1,
              minWidth: 0,
            },
            '& .embedded-form-input__label': {
              display: 'block',
              maxWidth: '100%',
              overflow: 'visible',
              textOverflow: 'clip',
              whiteSpace: 'normal',
              wordBreak: 'normal',
              overflowWrap: 'normal',
              lineHeight: 1.25,
            },
            '& .embedded-form-input__label--secondary': {
              marginTop: '3px',
            },
            '& .embedded-form-input__label--truncate': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& .embedded-form-input__status': {
              'flexShrink': 0,
              'minWidth': 0,
              '& .MuiTypography-root': {
                whiteSpace: 'nowrap',
              },
            },
            '& .embedded-form-input__summary': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginLeft: 'auto',
              flexShrink: 0,
              columnGap: '10px',
            },
            '& .embedded-form-input__summary-icon': {
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'width': '14px',
              'minWidth': '14px',
              '& svg, & .svg-img': {
                marginRight: '0 !important',
              },
            },
            '& .embedded-form-input__summary-input': {
              width: 'clamp(100px, 13vw, 130px)',
              minWidth: 'clamp(100px, 13vw, 130px)',
            },
            '& .MuiTextField-root': {
              width: 'calc(100% + 5px)',
            },
            '& .embedded-form-input__summary-column .MuiTextField-root': {
              width: '100%',
            },
            '& .embedded-form-input__summary-column .MuiInputBase-input': {
              fontWeight: 'normal',
            },
            '& .embedded-form-input__summary-column--credit-card .MuiInputBase-input': {
              color: Colors.primaryBase,
            },
            '& .Mui-error': {
              textAlign: 'end',
              marginRight: '0px',
            },
            [theme.breakpoints.down('md')]: {
              '& .embedded-form-input__label': {
                lineHeight: 1.2,
              },
            },
            [theme.breakpoints.down('sm')]: {
              '& .embedded-form-input__content': {
                paddingRight: '12px',
              },
              '& .embedded-form-input__status': {
                '& .MuiTypography-root': {
                  display: 'none',
                },
              },
              '& .embedded-form-input__summary': {
                columnGap: '10px',
              },
              '& .embedded-form-input__summary-icon': {
                width: '12px',
                minWidth: '12px',
              },
            },
          }),
          ...(ownerState.className === 'budget-details-averages' && {
            '& > :not(:last-child)': {
              marginBottom: '10px',
            },
          }),
          ...(ownerState.className === 'budget-details-panel-ghost' && {
            'display': 'flex',
            'justifyContent': 'space-between',
            'maxWidth': '1200px !important',
            'flexBasis': '100% !important',
            'padding': '10px',
            ...Borders.borderRadius_10,
            ...Alignments.verticalAlignCenter,
            '& .budget-details-panel-start': {
              'display': 'flex',
              '& svg, & .svg-img': {
                marginRight: '10px',
              },
            },
            '& .budget-details-panel-content': {
              'display': 'flex',
              'justifyContent': 'center',
              'minHeight': '40px',
              'borderRight': `2px solid ${Colors.primaryLight}`,
              'borderLeft': `2px solid ${Colors.primaryLight}`,
              '& > :not(:first-child)': {
                marginLeft: '5px',
              },
            },
            '& .budget-details-panel-end': {
              justifyContent: 'end',
            },
          }),
          ...(ownerState.className === 'budget-details-panel' && {
            'display': 'flex',
            'justifyContent': 'space-between',
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundColor': Colors.tertiaryBase,
            'maxWidth': '1200px !important',
            'flexBasis': '100% !important',
            'padding': '10px',
            ...Borders.borderRadius_10,
            ...Alignments.verticalAlignCenter,
            '& .budget-details-panel-start': {
              'display': 'flex',
              '& svg, & .svg-img': {
                marginRight: '10px',
              },
            },
            '& .budget-details-panel-content': {
              'display': 'flex',
              'justifyContent': 'center',
              'minHeight': '40px',
              'borderRight': `2px solid ${Colors.primaryLight}`,
              'borderLeft': `2px solid ${Colors.primaryLight}`,
              '& > :not(:first-child)': {
                marginLeft: '5px',
              },
            },
            '& .budget-details-panel-end': {
              justifyContent: 'end',
            },
          }),
          ...(ownerState.className === 'spending-banners-row' && {
            // Establishes a container-query context so the row below can react to
            // its OWN rendered width (e.g. a narrow side panel), not the viewport.
            containerType: 'inline-size',
            marginTop: '12px',
            marginBottom: '20px',
          }),
          ...(ownerState.className === 'spending-banners-row__inner' && {
            'display': 'flex',
            'alignItems': 'stretch',
            'columnGap': '15px',
            '@container (max-width: 685px)': {
              'flexDirection': 'column',
              'rowGap': '10px',
              '& .paid-by-credit-card-banner': {
                order: 2,
                minHeight: '38px',
              },
              '& .cash-and-debt-payments-banner': {
                order: 1,
                minHeight: '38px',
              },
              '& .paid-by-credit-card-banner__label': {
                width: 'auto',
                maxWidth: 'none',
                whiteSpace: 'nowrap',
              },
              '& .cash-and-debt-payments-banner__label': {
                width: 'auto',
                maxWidth: 'none',
                whiteSpace: 'nowrap',
              },
            },
          }),
          ...(ownerState.className === 'paid-by-credit-card-banner' && {
            'flex': '1 1 0',
            'backgroundColor': Colors.primaryLightest,
            'borderRadius': '4px',
            'padding': '4px 16px 4px 0px',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'space-between',
            'cursor': 'pointer',
            '&:hover': {
              backgroundColor: Colors.primaryLighter,
            },
            '& .paid-by-credit-card-banner__rail': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '60px',
              flex: '0 0 60px',
              paddingLeft: '25px',
            },
            '& .paid-by-credit-card-banner-left': {
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            },
            '& .paid-by-credit-card-banner__label': {
              width: '125px',
              maxWidth: '125px',
            },
            '& .paid-by-credit-card-banner-right': {
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              minWidth: 0,
            },
            '& .paid-by-credit-card-banner__chevron': {
              marginRight: '-10px',
            },
          }),
          ...(ownerState.className === 'cash-and-debt-payments-banner' && {
            'flex': '1 1 0',
            'backgroundColor': Colors.primaryLightest,
            'borderRadius': '4px',
            'padding': '4px 16px 4px 0px',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'space-between',
            '& .cash-and-debt-payments-banner__rail': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '60px',
              flex: '0 0 60px',
              paddingLeft: '25px',
            },
            '& .cash-and-debt-payments-banner-left': {
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            },
            '& .cash-and-debt-payments-banner__label': {
              width: '100px',
              maxWidth: '100px',
            },
            '& .cash-and-debt-payments-banner-right': {
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              minWidth: 0,
            },
          }),
          ...(ownerState.className === 'category-select-search-wrapper' && {
            'width': '100%',
            'marginTop': '12px',
            '& .add-an-item-search .MuiOutlinedInput-root': {
              'background': Colors.secondaryLightest,
              'borderRadius': '4px',
              'padding': '0 10px 0 20px',
              '& .MuiOutlinedInput-input': {
                fontSize: '16px',
                padding: '10px 0px 10px 8px',
              },
              '& .MuiOutlinedInput-input::placeholder': {
                fontSize: '16px',
              },
              '& fieldset': {
                border: `1px solid ${Colors.primaryLightest}`,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: Colors.primaryLightest,
                borderWidth: '1px',
              },
              '&:hover': {
                background: Colors.primaryLightest,
              },
              '&:hover fieldset': {
                borderColor: Colors.primaryLighter,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: Colors.primaryLighter,
              },
              '&.Mui-focused fieldset': {
                borderColor: Colors.primaryBase,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: Colors.primaryBase,
                borderWidth: '2px',
              },
            },
            '& .add-an-item-search.is-active .MuiOutlinedInput-root fieldset': {
              borderColor: Colors.primaryBase,
            },
            '& .add-an-item-search.is-active .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
              {
                borderColor: Colors.primaryBase,
                borderWidth: '2px',
              },
            '& .add-an-item-search.is-active .MuiOutlinedInput-root:hover fieldset': {
              borderColor: Colors.primaryBase,
            },
            '& .add-an-item-search.is-active .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
              {
                borderColor: Colors.primaryBase,
                borderWidth: '2px',
              },
          }),
          ...(ownerState.className === 'budget-search-results-column' && {
            width: '100%',
            minWidth: 0,
            flexBasis: 0,
            flexGrow: 1,
          }),
          ...(ownerState.className === 'content-card-container' && {
            'marginTop': '20px',
            '& > .content-card-header': {
              '& > svg, & > .svg-img': {
                marginRight: '10px',
              },
              '& > .column-header': {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                marginLeft: '20px',
              },
            },
            '& > .MuiCard-root': {
              marginTop: '15px',
            },
          }),
          ...(ownerState.className?.includes('info-card-border') && {
            '& > .MuiCard-root': {
              'padding': '20px',
              'boxShadow': 'none',
              'border': `1px solid ${Colors.primaryLight}`,
              'display': 'flex',
              'alignItems': 'center',
              '& > svg:first-child, & > .svg-img:first-child': {
                marginRight: '15px',
              },
            },
          }),
          ...(ownerState.className?.includes('info-card') && {
            'height': 'fit-content',
            '& :hover': {
              cursor: 'pointer',
              backgroundColor: Colors.disabledLight,
            },
            'minWidth': '250px',
          }),

          ...(ownerState.className === 'mb-plan-help-card' && {
            '& > .MuiCard-root': {
              'display': 'flex',
              'padding': '20px',
              'boxShadow': 'none',
              'border': `1px solid ${Colors.primaryLightest}`,
              'alignItems': 'center',
              '& > svg:first-child, & > .svg-img:first-child': {
                marginRight: '15px',
              },
            },
            'display': 'flex',
            'marginLeft': '45px',
          }),
          ...(ownerState.className === 'wizard-footer' && {
            '& .MuiButton-root': {
              whiteSpace: 'nowrap',
              minWidth: '150px',
            },
          }),
          ...(ownerState.className === 'mortgage-select' && {
            '& .embedded-list': {
              'margin': '18px 0',
              '& .embedded-list-content': {
                'backgroundColor': Colors.tertiaryBase,
                'border': `solid 1px ${Colors.primaryLightest}`,
                '& .embedded-list-content-title': {
                  color: Colors.secondaryBase,
                },
                '& .embedded-list-content-summary': {
                  color: Colors.errorBase,
                  whiteSpace: 'nowrap',
                },
              },
            },
            '& .embedded-list-content:hover': {
              backgroundColor: Colors.disabledLight,
            },
            '& .empty': {
              '& .embedded-list-header': {
                'justifyContent': 'center',
                '& .start-header': {
                  flexGrow: 1,
                  justifyContent: 'end',
                },
              },
              'backgroundColor': Colors.tertiaryBase,
              'border': `solid 1px ${Colors.primaryLightest}`,
              ...Borders.borderRadius_7,
            },
            '& .not-empty': {
              maxWidth: '50%',
            },
          }),
          ...(ownerState.className === 'goal-summary' && {
            'maxWidth': '550px',
            'width': '100%',
            'margin': '0 auto',
            '& .MuiCard-root': {
              width: '100%',
              height: '130px',
              marginTop: '15px',
              padding: '15px 30px 35px 30px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 'none',
              backgroundColor: Colors.tertiaryBase,
              border: `solid 1px ${Colors.primaryLightest}`,
              borderRadius: '12px',
            },
          }),
          ...(ownerState.className === 'investment-account-results-header' && {
            'width': '50%',
            'margin': '10px 0',
            'padding': '10px 20px',
            '& > .compare': {
              'cursor': 'pointer',
              'display': 'flex',
              'alignItems': 'center',
              '& > .svg-wrapper': {
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
            },
          }),
          ...(ownerState.className === 'investment-results-asset-breakdown' && {
            'display': 'flex',
            'marginTop': '12px',
            'padding': '0 20px',
            'marginLeft': '46px',
            '& > .MuiGrid-root': {
              width: '50%',
            },
            '& .amount-breakdown': {
              fontFamily: 'Roboto, system-ui, -apple-system, sans-serif',
              fontWeight: 'normal',
              fontSize: '16px',
            },
          }),
          ...(ownerState.className === 'investment-account-results' && {
            '& .or-divider': {
              margin: '20px 0',
            },
            '& .help-text': {
              margin: '20px 0',
            },
            '& .collapsible-panel': {
              '& .MuiCardContent-root': {
                paddingBottom: '22px',
              },
              '& .collapsible-panel-header': {
                'width': '100%',
                'display': 'flex',
                'justifyContent': 'flex-end',
                'padding': '11px 20px',
                'border': `solid 1px ${Colors.primaryLightest}`,
                'borderRadius': '12px',
                // Override the global role="expanded" rule that squares the bottom corners
                'borderBottomLeftRadius': '12px !important',
                'borderBottomRightRadius': '12px !important',
                '& :last-child': {
                  flexGrow: '0',
                },
                '& .collapsible-panel-summary': {
                  fontSize: '22px',
                  fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
                  fontWeight: 'normal',
                },
                '& .MuiCardHeader-content': {
                  'width': '50%',
                  '& .embedded-list-content-title': {
                    'color': Colors.secondaryBase,
                    'fontSize': '18px',
                    'textTransform': 'uppercase',
                    '& svg, & .svg-img': {
                      marginRight: '10px',
                    },
                  },
                  '& .embedded-list-content-summary': {
                    color: Colors.primaryBase,
                    fontSize: '22px',
                    fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
                    width: '50%',
                    justifyContent: 'end',
                  },
                },
                '& .MuiCardHeader-action': {
                  'width': '50%',
                  '& .embedded-list-content-title': {
                    'color': Colors.secondaryBase,
                    'fontSize': '18px',
                    'textTransform': 'uppercase',
                    '& svg, & .svg-img': {
                      marginRight: '10px',
                    },
                  },
                },
              },
            },
          }),
          ...(ownerState.className === 'brokerage-account-info-cards' && {
            'marginTop': '20px',
            '& .info-card': {
              marginTop: '20px',
            },
            '& .info-card:not(:last-child)': {
              marginRight: '20px',
            },
          }),
          ...(ownerState.className?.includes('progress-indicator') && {
            // 'marginRight': '50px',
            'width': '190px',
            'position': 'relative',
            'display': 'flex',
            'flexDirection': 'column',
            'alignItems': 'end',
            '& .MuiDivider-root': {
              borderColor: Colors.primaryLight,
              marginRight: '9px',
              flexGrow: 1,
            },
            '& .progress-item': {
              'fontSize': '18px',
              'width': 'max-content',
              'display': 'flex',
              'justifyContent': 'end',
              'alignItems': 'start',
              '.progress-item-content': {
                'marginLeft': '10px',
                'marginRight': '10px',
                'marginBottom': '40px',
                'display': 'flex',
                'flexDirection': 'column',
                'alignItems': 'end',
                '& .progress-item-title': {
                  overflow: 'hidden',
                  lineHeight: 1.2,
                  top: '-5px',
                  position: 'relative',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  maxWidth: '200px',
                },
                '& .progress-item-child': {
                  marginTop: '5px',
                  display: 'block',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  maxWidth: '170px',
                },
              },
              '& svg, & .svg-img': {
                'zIndex': 1,
                'color': Colors.primaryLight,
                'fontSize': '15px',
                'margin': '-2px 2px',
                '&:hover': {
                  fontSize: '17px',
                  margin: '-2px 1px',
                  cursor: 'pointer',
                },
              },
              '&.disabled svg, &.disabled .svg-img': {
                '&:hover': {
                  fontSize: '15px !important',
                  margin: '-2px 2px !important',
                  cursor: 'default !important',
                },
              },
              '& .active': {
                color: Colors.secondaryBase,
              },
              '& .progress-item-content:has(> :is(.progress-item-title:hover)) + *': {
                '& svg, & .svg-img': {
                  fontSize: '17px',
                  margin: '-2px 1px',
                },
              },
              '&.disabled .progress-item-content:has(> :is(.progress-item-title:hover)) + *': {
                '& svg, & .svg-img': {
                  fontSize: '15px !important',
                  margin: '-2px 2px !important',
                },
              },
              '& .progress-item-content > *': {
                '&:hover': {
                  cursor: 'pointer',
                  textDecoration: 'underline',
                },
              },
              '&.disabled .progress-item-content > *': {
                '&:hover': {
                  cursor: 'default !important',
                  textDecoration: 'none !important',
                },
              },
            },
          }),
          ...(ownerState.className === 'alert-switch-option' && {
            backgroundColor: Colors.tertiaryBase,
            border: `solid 1px ${Colors.primaryLightest}`,
            padding: '10px',
            borderRadius: '12px',
            width: '315px',
          }),
          ...(ownerState.className === '3NU-overview-content' && {
            '& .panel-popover': {
              'flexGrow': 1,
              'maxWidth': 'none',
              '& > .MuiCard-root': {
                padding: '16px 20px',
              },
            },
            '& .group-panels': {
              marginTop: '0px',
            },
            '& .panel': {
              '& .MuiTypography-root': {
                cursor: 'auto !important',
              },
            },
            '& .group-panel-container': {
              'display': 'flex',
              'flexDirection': 'column',
              'rowGap': '20px',
              '& .MuiCard-root': {
                boxShadow: 'none',
                border: `solid 1px ${Colors.primaryLightest}`,
              },
            },
          }),
          ...(ownerState.className === 'lms-overview-content' && {
            '& .panel-popover': {
              '& > .MuiCard-root': {
                padding: '16px 20px',
              },
            },
            '& .group-panels': {
              marginTop: '0px',
            },
            '& .panel': {
              '& .MuiTypography-root': {
                cursor: 'auto !important',
              },
            },
            '& .group-panel-container': {
              'display': 'flex',
              'flexDirection': 'column',
              'rowGap': '20px',
              '& .MuiCard-root': {
                boxShadow: '0 4px 7px 1px rgba(30, 41, 105, 0.2)',
                border: `solid 1px ${Colors.primaryLightest}`,
              },
            },
          }),
          ...(ownerState.className === 'advice-section' && {
            '& .panel-popover': {
              'flexGrow': 1,
              'maxWidth': 'none',
              'marginTop': '10px',
              '& > .MuiCard-root': {
                padding: '16px 20px',
              },
            },
            '& .collapsible-panel': {
              '& .MuiCardContent-root': {
                padding: '10px 20px',
              },
            },
          }),
          ...(ownerState.className === 'advice-budget-section' && {
            border: `solid 1px ${Colors.primaryLightest}`,
            backgroundColor: Colors.tertiaryBase,
            maxWidth: '1200px !important',
            flexBasis: '100% !important',
            padding: Spacing.md,
            ...Borders.borderRadius_10,
            ...Alignments.verticalAlignCenter,
          }),
          ...(ownerState.className === '3NU-section' && {
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundColor': Colors.tertiaryBase,
            'display': 'flex',
            'flexDirection': 'column',
            'alignItems': 'center',
            'maxWidth': '1200px !important',
            'padding': Spacing.md,
            'paddingBottom': '0px',
            ...Borders.borderRadius_10,
            '& .content-section': {
              'justifyContent': 'space-between',
              ...Alignments.verticalAlignCenter,
              '& .course-details': {
                'maxWidth': '30%',
                '&.wide': {
                  maxWidth: '65%',
                },
              },
            },
          }),
          ...(ownerState.className === 'lms-course-list' && {
            'backgroundColor': Colors.tertiaryBase,
            'display': 'flex',
            'flexDirection': 'column',
            'justifyContent': 'space-between',
            'alignItems': 'center',
            'maxWidth': '1200px !important',
            'height': '100%',
            'paddingBottom': '0px',
            ...Borders.borderRadius_10,
            '&:hover': {
              backgroundColor: Colors.disabledLight,
            },
            '& .content-section': {
              'justifyContent': 'space-between',
              'padding': Spacing.md,
              'width': '100%',
              ...Alignments.verticalAlignCenter,
              '& .course-details': {
                maxWidth: '30%',
              },
            },
          }),
          ...(ownerState.className === 'walk-through-advice-card' && {
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundImage': 'linear-gradient(to bottom, rgba(231, 247, 250, 0.27), #89d2e2)',
            'display': 'flex',
            'flexWrap': 'nowrap',
            'justifyContent': 'space-between',
            'alignItems': 'end',
            '& > img': {
              width: '50%',
              borderRadius: 'inherit',
            },
            ...Borders.borderRadius_10,
          }),
          ...(ownerState.className === '3nu-next-lesson-card' && {
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundImage': 'linear-gradient(to bottom, #ededed -61%, #cfe1f2)',
            'display': 'flex',
            'flexWrap': 'nowrap',
            'justifyContent': 'space-between',
            'alignItems': 'stretch',
            '& > .MuiGrid-root': {
              display: 'flex',
              alignItems: 'center',
              background: '#f7d27e',
              flexGrow: 1,
            },
            '& > .left-grid': {
              borderTopLeftRadius: 'inherit',
              borderBottomLeftRadius: 'inherit',
            },
            '& > .course-details': {
              alignItems: 'start',
              flexDirection: 'column',
              justifyContent: 'center',
            },
            '& > .gradient': {
              background:
                'linear-gradient(to bottom right, #f7d27e, #f7d27e 50%, transparent 50%, transparent)',
              height: '150px',
              width: '150px',
              flexGrow: 0,
            },
            '& > img': {
              maxWidth: '230px',
              borderRadius: 'inherit',
            },
            ...Borders.borderRadius_10,
          }),
          ...(ownerState.className === 'lms-course-complete-lesson-card' && {
            'border': `solid 1px ${Colors.primaryLightest}`,
            'display': 'flex',
            'flexWrap': 'nowrap',
            'justifyContent': 'center',
            'alignItems': 'center',
            'boxShadow': '0 4px 7px 1px rgba(30, 41, 105, 0.2)',
            'position': 'relative',
            'overflow': 'hidden',
            'backgroundColor': Colors.tertiaryBase,
            '& > .content': {
              paddingTop: '5px',
              paddingBottom: '5px',
              paddingRight: '20px',
              display: 'flex',
              flexWrap: 'nowrap',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: Colors.tertiaryBase,
              zIndex: 1,
            },
            '& .background': {
              position: 'absolute',
            },
            ...Borders.borderRadius_10,
          }),
          ...(ownerState.className === 'lms-next-lesson-card' && {
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundColor': Colors.tertiaryBase,
            'display': 'flex',
            'flexWrap': 'nowrap',
            'justifyContent': 'space-between',
            'alignItems': 'stretch',
            'boxShadow': '0 4px 7px 1px rgba(30, 41, 105, 0.2)',

            '& > .MuiGrid-root': {
              display: 'flex',
              alignItems: 'center',
            },
            '& > .left-grid': {
              borderTopLeftRadius: 'inherit',
              borderBottomLeftRadius: 'inherit',
            },
            '& > .course-details': {
              alignItems: 'start',
              flexDirection: 'column',
              justifyContent: 'center',
              flexGrow: 1,
            },
            '& .end-adornment-wrapper': {
              'borderTopRightRadius': 'inherit',
              'borderBottomRightRadius': 'inherit',
              'display': 'flex',
              'position': 'relative',

              '& .gradient': {
                background: `linear-gradient(to bottom right, transparent, transparent 50%, ${Colors.primaryBase} 50%, ${Colors.primaryBase})`,
                minHeight: '120px',
                width: '150px',
                flexGrow: 0,
              },

              '& .start-lesson-banner': {
                height: '100%',
                flexGrow: 0,
                backgroundColor: Colors.primaryBase,
                borderTopRightRadius: 'inherit',
                borderBottomRightRadius: 'inherit',
                display: 'flex',
                paddingLeft: '35px',
                paddingRight: '65px',
                position: 'relative',
              },
            },
            '& > img': {
              maxWidth: '230px',
              borderRadius: 'inherit',
            },
            ...Borders.borderRadius_10,
          }),
          ...(ownerState.className === 'unit-section' && {
            '& .collapsible-panel': {
              '& .MuiCardContent-root': {
                padding: '10px 20px',
              },
            },
          }),
          ...(ownerState.className === 'unit-progress-card-lesson' && {
            ...Borders.borderRadius_10,
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'space-between',
            'width': '100%',
            'border': `1px solid ${Colors.primaryLightest}`,
            'backgroundColor': Colors.secondaryLightest,
            'padding': '10px',
            '& .unit-progress-card-details': {
              'alignItems': 'center',
              'columnGap': '10px',
              'display': 'flex',
              'minWidth': 0,
              '& > svg, & > .svg-img': {
                width: '30px',
                height: '30px',
              },
            },
            '& .show-on-hover': {
              display: 'none',
            },
            '&:hover': {
              'cursor': 'pointer',
              '& .lesson-title': {
                textDecoration: 'underline',
              },
              '& .show-on-hover': {
                display: 'inherit',
              },
              '& .hide-on-hover': {
                display: 'none',
              },
            },
            '& .hide-on-hover': {
              display: 'inherit',
            },
          }),
          ...(ownerState.className === 'course-progress-side-view' && {
            ...Borders.borderRadius_10,
            'border': `1px solid ${Colors.primaryLightest}`,
            'backgroundColor': '#fff',
            'height': 'fit-content',
            'maxHeight': '70vh',
            '& .MuiTableContainer-root': {
              maxHeight: '70vh',
            },
            '& .MuiCardContent-root': {
              paddingBottom: '0px !important',
            },
            '& .collapsible-panel': {
              'marginBottom': '0px',
              '&:not(:last-child)': {
                borderBottom: `solid 1px ${Colors.primaryLightest}`,
              },
            },
            '& .unit-progress-card-lesson': {
              'width': '100%',
              'borderRadius': '0px',
              'border': 'none',
              '&:first-child': {
                borderTop: `solid 1px ${Colors.primaryLightest}`,
              },
            },
          }),
          ...(ownerState.className === 'tools-split-card' && {
            'backgroundImage': 'linear-gradient(to bottom, #cfe1f2 -61%, #accae8)',
            'display': 'flex',
            'flexWrap': 'nowrap',
            '& > .MuiGrid-root': {
              display: 'flex',
              alignItems: 'center',
              background: Colors.disabledLight,
            },
            '& > .left-grid': {
              borderTopLeftRadius: 'inherit',
              borderBottomLeftRadius: 'inherit',
            },
            '& > .img-wrapper': {
              'position': 'relative',
              '&::before': {
                content: '""',
                background: Colors.disabledLight,
                position: 'absolute',
                clipPath: 'polygon(-1% 0, 25% 0, 0 100%)',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2,
              },
              '& > img': {
                maxWidth: '100%',
                height: '100%',
                borderRadius: 'inherit',
              },
            },
            ...Borders.borderRadius_10,
          }),
          ...(ownerState.className === 'forward-slash-split-card' && {
            'width': '100%',
            'overflow': 'hidden',
            'display': 'flex',
            'flexWrap': 'nowrap',
            'background': 'var(--background)',
            '& > .MuiGrid-root': {
              display: 'flex',
              alignItems: 'center',
              background: 'var(--background)',
            },
            '& .left-grid': {
              'display': 'flex',
              'position': 'relative',
              'alignContent': 'center',
              'justifyContent': 'center',
              'borderTopLeftRadius': 'inherit',
              'borderBottomLeftRadius': 'inherit',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: -150,
                width: '150px',
                height: '100%',
                background: 'var(--background)',
                clipPath: 'polygon(-1% 0, 100% 0, 0% 100%)',
                zIndex: 2,
              },
            },
            '& > .img-wrapper': {
              'display': 'flex',
              'position': 'relative',
              'borderBottomRightRadius': 'inherit',
              'borderTopRightRadius': 'inherit',
              'overflow': 'hidden',
              '& img': {
                display: 'flex',
                overflow: 'hidden',
                maxHeight: '100%',
                borderRadius: 'inherit',
              },
            },
            ...Borders.borderRadius_10,
          }),
          ...(ownerState.className === 'back-slash-split-card' && {
            'width': '100%',
            'height': '100%',
            'display': 'flex',
            'flexWrap': 'nowrap',
            'overflow': 'hidden',
            'background': 'var(--background)',
            '& > .MuiGrid-root': {
              display: 'flex',
              alignItems: 'center',
              background: 'var(--background)',
            },
            '& > .img-wrapper': {
              'display': 'flex',
              'position': 'relative',
              'borderBottomLeftRadius': 'inherit',
              'borderTopLeftRadius': 'inherit',
              'overflow': 'hidden',
              '& > img': {
                width: '100%',
                maxHeight: '100%',
                borderRadius: 'inherit',
              },
            },
            '& .right-grid': {
              'display': 'flex',
              'position': 'relative',
              'width': '100%',
              'alignContent': 'center',
              'justifyContent': 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: -50,
                width: '50px',
                height: '100%',
                background: 'var(--background)',
                clipPath: 'polygon(101% 0, 0 0, 101% 100%)',
                zIndex: 2,
              },
            },
            ...Borders.borderRadius_10,
          }),
          ...(ownerState.className === 'holistic-advice-view-content' && {
            '& .advice-section': {
              marginTop: Spacing.lg,
            },
            '& .left-container': {
              marginRight: Spacing.xxl,
              // flexGrow: 1,
              width: '67%',
            },
            '& .right-container': {
              // flexGrow: 2,
              width: '33%',
            },
          }),
          ...(ownerState.className === 'goal-card-details-summary' && {
            ...Borders.borderRadius_10,
            border: `1px solid ${Colors.primaryLightest}`,
            backgroundColor: Colors.secondaryLightest,
            display: 'flex',
            flexDirection: 'column',
            paddingTop: '10px',
            paddingBottom: '10px',
            alignItems: 'center',
          }),
          ...(ownerState.className === 'transactions-selector-panel' && {
            'color': Colors.secondaryBase,
            'fontSize': '12px',
            'background': Colors.primaryLightest,
            'borderRadius': '7px',
            'paddingLeft': '5px',
            'paddingRight': '5px',
            'alignItems': 'center',
            'display': 'flex',
            'columnGap': '10px',
            '&:hover': {
              cursor: 'pointer',
              background: Colors.primaryLighter,
            },
          }),
          ...(ownerState.className === 'transactions-selectors' && {
            'marginTop': '20px',
            'marginBottom': '20px',
            'display': 'flex',
            'columnGap': '10px',
            '& > *': {
              flexGrow: 1,
            },
          }),
          ...(ownerState.className === 'category-select-container' && {
            display: 'flex',
            justifyContent: 'flex-start',
            columnGap: '4%',
            rowGap: '10px',
          }),
          ...(ownerState.className ===
            'category-select-container category-select-container-compact' && {
            display: 'flex',
            justifyContent: 'flex-start',
            columnGap: '4%',
            rowGap: '10px',
            maxHeight: '320px',
            overflowY: 'auto',
            alignContent: 'flex-start',
            paddingRight: '4px',
          }),
          ...(ownerState.className ===
            'account-select-container account-select-container-compact' && {
            display: 'flex',
            justifyContent: 'flex-start',
            columnGap: '4%',
            maxHeight: '200px',
            overflowY: 'auto',
            alignContent: 'flex-start',
            paddingRight: '4px',
          }),
          ...(ownerState.className ===
            'category-filter-select-container category-filter-select-container-compact' && {
            display: 'flex',
            justifyContent: 'flex-start',
            columnGap: '4%',
            maxHeight: '200px',
            overflowY: 'auto',
            alignContent: 'flex-start',
          }),
          ...(ownerState.className === 'category-select-all' && {
            'borderRadius': '10px',
            'border': `solid 1px transparent`,
            'padding': '10px',
            'display': 'flex',
            'justifyContent': 'space-between',
            'width': '48%',
            '&:hover': {
              background: Colors.primaryLightest,
              cursor: 'pointer',
            },
            '& .MuiCheckbox-root': {
              padding: '0px',
              color: Colors.secondaryBase,
            },
            '& .MuiCheckbox-root.Mui-checked': {
              color: Colors.secondaryBase,
            },
            '& .MuiCheckbox-root.MuiCheckbox-indeterminate': {
              color: Colors.secondaryBase,
            },
            '& .MuiTypography-root': {
              color: Colors.secondaryBase,
            },
          }),
          ...(ownerState.className === 'account-select-all' && {
            'borderRadius': '10px',
            'display': 'flex',
            'justifyContent': 'space-between',
            'width': '48%',
            'border': `solid 1px transparent`,
            '&:hover': {
              border: `solid 1px ${Colors.primaryLightest}`,
              background: Colors.secondaryLightest,
              cursor: 'pointer',
            },
            '& .MuiCheckbox-root': {
              padding: '0px',
              color: Colors.secondaryBase,
            },
            '& .MuiCheckbox-root.Mui-checked': {
              color: Colors.secondaryBase,
            },
            '& .MuiCheckbox-root.MuiCheckbox-indeterminate': {
              color: Colors.secondaryBase,
            },
            '& .MuiTypography-root': {
              color: Colors.secondaryBase,
            },
          }),
          ...(ownerState.className === 'category-filter-select-all' && {
            'display': 'flex',
            'justifyContent': 'space-between',
            'width': '48%',
            'border': `solid 1px transparent`,
            '&:hover': {
              border: `solid 1px ${Colors.primaryLightest}`,
              background: Colors.secondaryLightest,
              cursor: 'pointer',
            },
            '& .MuiCheckbox-root': {
              padding: '0px',
              color: Colors.secondaryBase,
            },
            '& .MuiCheckbox-root.Mui-checked': {
              color: Colors.secondaryBase,
            },
            '& .MuiCheckbox-root.MuiCheckbox-indeterminate': {
              color: Colors.secondaryBase,
            },
            '& .MuiTypography-root': {
              color: Colors.secondaryBase,
            },
          }),
          ...(ownerState.className === 'category-select' && {
            'borderRadius': '10px',
            'border': `solid 1px ${Colors.primaryLightest}`,
            'padding': '10px',
            'display': 'flex',
            'justifyContent': 'space-between',
            'width': '48%',
            '&:hover': {
              background: Colors.primaryLightest,
              cursor: 'pointer',
            },
            '& .MuiCheckbox-root': {
              padding: '0px',
              color: Colors.primaryLight,
            },
            '& .MuiTypography-root': {
              color: Colors.primaryBase,
            },
          }),
          ...(ownerState.className === 'account-select' && {
            'borderRadius': '10px',
            'display': 'flex',
            'justifyContent': 'space-between',
            'width': '48%',
            'color': Colors.secondaryBase,
            'border': `solid 1px transparent`,
            '&:hover': {
              border: `solid 1px ${Colors.primaryLightest}`,
              backgroundColor: Colors.disabledLight,
              cursor: 'pointer',
            },
            '& .MuiCheckbox-root': {
              padding: '0px',
              color: Colors.primaryBase,
            },
            '& .MuiTypography-root': {
              color: Colors.secondaryBase,
            },
          }),
          ...(ownerState.className === 'category-filter-select' && {
            'display': 'flex',
            'justifyContent': 'space-between',
            'width': '48%',
            'color': Colors.secondaryBase,
            'border': `solid 1px transparent`,
            '&:hover': {
              border: `solid 1px ${Colors.primaryLightest}`,
              backgroundColor: Colors.disabledLight,
              cursor: 'pointer',
            },
            '& .MuiCheckbox-root': {
              padding: '0px',
              color: Colors.primaryBase,
            },
            '& .MuiTypography-root': {
              color: Colors.secondaryBase,
            },
          }),
          ...(ownerState.className === 'category-select-unselected-hover' && {
            'borderRadius': '10px',
            'border': `solid 1px ${Colors.primaryLightest}`,
            'padding': '10px',
            'display': 'flex',
            'justifyContent': 'space-between',
            'width': '48%',
            '&:hover': {
              background: Colors.disabledLight,
              cursor: 'pointer',
            },
            '& .MuiCheckbox-root': {
              padding: '0px',
              color: Colors.primaryLight,
            },
            '& .MuiTypography-root': {
              color: Colors.primaryBase,
            },
          }),
          ...(ownerState.className === 'category-select-selected' && {
            'borderRadius': '10px',
            'border': `solid 1px ${Colors.primaryLightest}`,
            'padding': '10px',
            'display': 'flex',
            'justifyContent': 'space-between',
            'width': '48%',
            'background': Colors.primaryBase,
            '&:hover': {
              background: Colors.primaryLight,
              cursor: 'pointer',
            },
            '& .MuiCheckbox-root': {
              padding: '0px',
              color: Colors.tertiaryBase,
            },
            '& .MuiTypography-root': {
              color: Colors.tertiaryBase,
            },
          }),
          ...(ownerState.className === 'account-select-selected' && {
            'borderRadius': '10px',
            'display': 'flex',
            'justifyContent': 'space-between',
            'width': '48%',
            'color': Colors.secondaryBase,
            'border': `solid 1px transparent`,
            '&:hover': {
              border: `solid 1px ${Colors.primaryLightest}`,
              backgroundColor: Colors.disabledLight,
              cursor: 'pointer',
            },
            '& .MuiCheckbox-root': {
              padding: '0px',
              color: Colors.primaryBase,
            },
            '& .MuiCheckbox-root.Mui-checked': {
              color: Colors.primaryBase,
            },
            '& .MuiTypography-root': {
              color: Colors.secondaryBase,
            },
          }),
          ...(ownerState.className === 'category-filter-select-selected' && {
            'display': 'flex',
            'justifyContent': 'space-between',
            'width': '48%',
            'color': Colors.secondaryBase,
            'border': `solid 1px transparent`,
            '&:hover': {
              border: `solid 1px ${Colors.primaryLightest}`,
              backgroundColor: Colors.disabledLight,
              cursor: 'pointer',
            },
            '& .MuiCheckbox-root': {
              padding: '0px',
              color: Colors.primaryBase,
            },
            '& .MuiCheckbox-root.Mui-checked': {
              color: Colors.primaryBase,
            },
            '& .MuiTypography-root': {
              color: Colors.secondaryBase,
            },
          }),
          ...(ownerState.className === 'category-add-custom-item-button' && {
            'marginTop': '12px',
            'marginBottom': '4px',
            'height': '40px',
            'borderRadius': '8px',
            'textTransform': 'none',
            'backgroundColor': Colors.primaryLightest,
            'color': Colors.primaryBase,
            '&:hover': {
              backgroundColor: Colors.primaryLighter,
            },
          }),
          ...(ownerState.className === 'spending-overview-categories-container' && {
            // Top-align the dot with the first line so long, wrapping labels
            // (e.g. the Spanish "Gobierno y organizaciones sin fines de lucro")
            // stay aligned with the shorter label in the adjacent column.
            'display': 'flex',
            'alignItems': 'flex-start',
            '& .circle': {
              height: '15px',
              flexShrink: 0,
              transition: 'height 0.3s',
            },
            '& .category': {
              // minWidth: 0 lets the label shrink and wrap within its grid
              // column instead of overflowing into the next column;
              // overflowWrap breaks a long word if the column gets very narrow.
              marginLeft: '5px',
              minWidth: 0,
              overflowWrap: 'break-word',
            },
            '&:hover': {
              'cursor': 'pointer',
              '& .circle': {
                height: '20px',
              },
              '& .category': {
                textDecoration: 'underline',
              },
            },
          }),
          ...(ownerState.className === 'lifestyle-banner' && {
            'width': '973px',
            'height': '160px',
            'position': 'relative',
            'border': `solid 1px ${Colors.primaryLightest}`,
            'backgroundImage': 'linear-gradient(to bottom, #cfe1f2 -61%, #accae8)',
            'display': 'flex',
            'flexWrap': 'nowrap',
            'justifyContent': 'space-between',
            'alignItems': 'stretch',
            '& .MuiTypography-root': {
              zIndex: 3,
            },
            '& > .MuiGrid-root': {
              display: 'flex',
              justifyContent: 'center',
              background: '#8e94b4',
              flexGrow: 1,
            },
            '& > .left-grid': {
              borderTopLeftRadius: 'inherit',
              borderBottomLeftRadius: 'inherit',
            },
            '& > .gradient': {
              position: 'absolute',
              top: 0,
              right: 0,
              background:
                'linear-gradient(to bottom right, #8e94b4, #8e94b4 50%, transparent 50%, transparent)',
              height: '100%',
              width: '75px',
              flexGrow: 0,
              zIndex: 2,
              marginRight: 210,
            },
            '& > img': {
              alignSelf: 'flex-end',
              zIndex: 1,
              minWidth: '285px',
              height: '70%',
              borderRadius: 'inherit',
              objectFit: 'contain',
            },

            ...Borders.borderRadius_10,
          }),
          ...(ownerState.className === 'lifestyle-group-panels' && {
            ...Alignments.alignCenter,
            '& .group-panels': {
              'display': 'grid',
              'gridTemplateColumns': 'repeat(3, 1fr)',
              'columnGap': '20px',
              'rowGap': '20px',
              '@media (max-width: 1300px)': {
                gridTemplateColumns: 'repeat(2, 1fr)',
              },
              '@media (max-width: 900px)': {
                gridTemplateColumns: '1fr',
              },
            },
            '& .MuiCard-root': {
              height: '122px',
              width: '311px',
              backdropFilter: 'blur(6px)',
              boxShadow:
                '0 4px 7px 1px rgba(30, 41, 105, 0.2), 0 -4px 7px 1px rgba(30, 41, 105, 0.2)',
            },
          }),
          ...(ownerState.className === 'member-login-card' && {
            ...Borders.borderRadius_10,
            backgroundColor: Colors.tertiaryBase,
            display: 'flex',
            flexDirection: 'column',
            paddingTop: '40px',
            paddingBottom: '20px',
            alignItems: 'center',
            width: '460px',
          }),
          ...(ownerState.className === 'tool-start-view' && {
            '& .panel-popover': {
              maxWidth: 'none',
              width: '300px',
            },
          }),
          ...(ownerState.className === 'tool-form-container' && {
            'display': 'flex',
            'flexDirection': 'column',
            'rowGap': '30px',
            '& .slider-input': {
              'width': '125px !important',
              '& input': { textAlign: 'start !important' },
            },
            '& .MuiSlider-root': {
              marginRight: '35px !important',
            },
          }),
          ...(ownerState.className?.includes('popup-info-card-border') && {
            '& > .MuiCard-root': {
              'padding': '20px',
              'boxShadow': 'none',
              'border': `1px solid ${Colors.disabledLight}`,
              'display': 'flex',
              'alignItems': 'center',
              '& > svg:first-child, & > .svg-img:first-child': {
                marginRight: '15px',
              },
            },
          }),
          ...(ownerState.className?.includes('popup-info-card') && {
            'height': 'fit-content',
            '& :hover': {
              backgroundColor: Colors.disabledLight,
            },
          }),
          ...(ownerState.className?.includes('most-popular-car-header') && {
            'justifyContent': 'space-between',
            'alignItems': 'center',
            '& .MuiCard-root': {
              borderColor: `${Colors.primaryLightest} !important`,
            },
          }),
          ...(ownerState.className === 'most-popular-car-container' && {
            'minHeight': '100px',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'space-between',
            'padding': '5px',
            'border': `solid 1px ${Colors.primaryLight}`,
            'backgroundColor': Colors.tertiaryBase,
            ...Borders.borderRadius_10,
            '& .MuiCard-root': {
              padding: '15px',
              margin: '0px 10px',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              border: `solid 1px ${Colors.primaryLighter}`,
              backgroundColor: Colors.secondaryLightest,
            },
          }),
          ...(ownerState.className === 'year-output' && {
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'space-between',
            'padding': '10px',
            '& .MuiCard-root': {
              padding: '10px',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              border: `solid 1px ${Colors.primaryLighter}`,
              backgroundColor: Colors.secondaryLightest,
            },
            '& .right-align': {
              fontSize: 16,
              fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
              fontWeight: 'bold',
              color: Colors.primaryBase,
              display: 'flex',
              justifyContent: 'right',
            },
          }),
          ...(ownerState.className === 'prefixed-select-input' && {
            display: 'flex',
            justifyContent: 'space-between',
            justifyItems: 'center',
            alignItems: 'center',
          }),
          ...(ownerState.className === 'search-view' && {
            'flexDirection': 'column',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            '& .MuiOutlinedInput-root': {
              'background': `${Colors.primaryLightest} !important`,
              '& fieldset': {
                borderWidth: '0px',
              },
              '&:hover fieldset, &:focus-within fieldset': {
                borderWidth: '0px',
                boxShadow: `0 1px 4px 0 ${Colors.primaryLightest}, 0 1px 4px 0 rgba(0, 0, 0, 0.5) !important`,
              },
            },
            '& .MuiInputBase-input': {
              'paddingTop': '10px !important',
              'paddingBottom': '10px !important',
              '&:hover fieldset, &:focus-within fieldset': {
                borderWidth: '0px',
                boxShadow: `0 1px 4px 0 ${Colors.primaryLightest}, 0 1px 4px 0 rgba(0, 0, 0, 0.5) !important`,
              },
            },
          }),
          ...(ownerState.className === 'charity-navigator-branding' && {
            'display': 'flex',
            'flexDirection': 'column',
            'justifyContent': 'center',
            'alignItems': 'center',
            '&:hover': {
              cursor: 'pointer',
            },
            '& > div': {
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
            '& > img': {
              width: '200px',
              height: 'auto',
            },
            '& .MuiTypography-root': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '60%',
              textAlign: 'center',
            },
          }),
          ...(ownerState.className === 'org-header' && {
            'alignItems': 'center',
            'justifyContent': 'space-between',
            'width': '100%',
            '& .left-column': {
              flex: 1,
              flexDirection: 'column',
              display: 'flex',
            },
            '& .org-name': {
              maxWidth: '1000px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            },
          }),
          ...(ownerState.className === 'clickable-contact-method' && {
            '&:hover': {
              cursor: 'pointer',
            },
          }),
          ...(ownerState.className === 'lms-content-header' && {
            'alignItems': 'center',
            'display': 'flex',
            '& .lms-content-icon': {
              'display': 'flex',
              'justifyContent': 'center',
              'alignItems': 'center',
              'paddingRight': '30px',
              '& > img': {
                width: '80px',
                height: '80px',
              },
            },
          }),
          ...(ownerState.className?.includes('past-invoice') && {
            '& > .MuiCard-root': {
              marginTop: '10px',
              padding: '12px 20px',
              boxShadow: 'none',
              border: `1px solid ${Colors.primaryLightest}`,
            },
          }),
          ...(ownerState.className === 'horizontal-line-icon' && {
            'position': 'relative',
            '& .horizontal-line': {
              position: 'absolute',
              top: '24px',
              left: -16,
              right: -16,
              height: '1px',
              backgroundColor: Colors.primaryLighter,
            },
            '& .icon-container': {
              border: `1px solid ${Colors.primaryLighter}`,
              borderRadius: '50%',
              width: '55px',
              height: '55px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: Colors.tertiaryBase,
              position: 'relative',
            },
            '.MuiCard-root:hover & .icon-container': {
              backgroundColor: Colors.disabledLight,
            },
          }),
          ...(ownerState.className?.includes('subscription-table-row') && {
            'flexDirection': 'row',
            'justifyContent': 'space-between',
            'alignItems': 'center',
            'padding': '0px 5px',
            '& .button-section': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '4%',
            },
            '& .description-section': {
              width: '33%',
            },
            '& .price-section': {
              width: '17%',
            },
            '& .price-section-org-pays': {
              width: '63%',
            },
            '& .duration-section': {
              width: '17%',
            },
            '& .origin-section': {
              width: '12%',
            },
            '& .date-section': {
              width: '17%',
            },
          }),
          ...(ownerState.className?.includes('payment-methods-table-row') && {
            'flexDirection': 'row',
            'justifyContent': 'space-between',
            'alignItems': 'center',
            'padding': '0px 5px',
            '& .button-section': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '4%',
            },
            '& .name-section': {
              width: '50%',
            },
            '& .type-section': {
              width: '25%',
            },
            '& .action-section': {
              width: '21%',
              paddingRight: 3,
              textAlign: 'end',
            },
          }),
          ...(ownerState.className?.includes('payment-history-table-row') && {
            'flexDirection': 'row',
            'justifyContent': 'space-between',
            'alignItems': 'center',
            'padding': '0px 5px',
            '& .button-section': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '4%',
            },
            '& .description-section': {
              width: '33%',
            },
            '& .amount-section': {
              width: '17%',
            },
            '& .payment-section': {
              width: '29%',
            },
            '& .date-section': {
              width: '17%',
            },
            '& .blank-section': {
              width: '8%',
              paddingRight: 1,
            },
          }),
          ...(ownerState.className === 'checkbox-with-custom-checkmark' && {
            'display': 'flex',
            'flexDirection': 'row',
            'alignItems': 'center',
            'cursor': 'pointer',
            '& .MuiPaper-root': {
              width: 26,
              height: 26,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: 'none',
              borderRadius: '2.5px',
            },
          }),
          ...(ownerState.className === 'income-replacement-options-row' && {
            display: 'flex',
            justifyContent: 'center',
            columnGap: '24px',
          }),
          ...(ownerState.className === 'slider-percent-custom-end-adornment' && {
            'backgroundColor': Colors.tertiaryBase,
            'width': '90px',
            'height': '33px',
            'padding': '5px 0 5px 14px',
            'borderRadius': '4px',
            'display': 'flex',
            'alignItems': 'center',
            '& .MuiTypography-root': {
              display: 'flex',
              alignItems: 'center',
            },
          }),
          ...(ownerState.className === 'off-target-retirement-explainer' && {
            'width': '100%',
            'display': 'flex',
            'flexDirection': 'column',
            'marginTop': 2,
            'gap': 4,
            '& .option-container': {
              'display': 'flex',
              'justifyContent': 'space-between',
              'alignItems': 'flex-end',
              'marginBottom': '10px',
              '& .icon-text-side': {
                'display': 'flex',
                'flexDirection': 'row',
                'alignItems': 'center',
                'justifyContent': 'center',
                'columnGap': '50px',
                'marginLeft': '15px',
                '& .text-container': {
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                },
              },
              '& .MuiButtonBase-root': {
                alignSelf: 'flex-end',
                marginBottom: '11px',
                minWidth: '215px',
                maxWidth: '215px',
              },
            },
          }),
          ...(ownerState.className === 'income-replacement-comparison-bar-chart' && {
            'flexDirection': 'row',
            'alignItems': 'flex-end',
            'justifyContent': 'center',
            'columnGap': Spacing.lg,
            '& .chart-label': {
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '6px',
            },
            '& .bar': {
              display: 'flex',
              justifyContent: 'center',
              padding: '5px',
              width: 157,
            },
          }),
          ...(ownerState.className === 'mobile-progress' && {
            'display': 'flex',
            'width': '100vw',
            'alignItems': 'center',
            'minHeight': '3px',
            'position': 'relative',
            'left': '50%',
            'transform': 'translateX(-50%)',
            'boxSizing': 'border-box',
            'paddingLeft': '20px',
            'paddingRight': '20px',
            'columnGap': '1px',
            '& .mobile-progress-item': {
              flex: 1,
              marginLeft: 2,
              marginRight: 2,
              marginBottom: '10px',
              height: '3px',
              borderRadius: 4,
            },
          }),
          ...(ownerState.className === 'budget-search-results-empty-state' && {
            border: `1px solid ${Colors.primaryLightest}`,
            borderRadius: '8px',
            backgroundColor: Colors.tertiaryBase,
            marginTop: '10px',
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
          }),
        }),
      },
    },
    MuiStack: {
      defaultProps: {
        sx: (theme) => ({
          '& .MuiSlider-root': {
            marginRight: Spacing.xxs,
          },
          '& .slider-input': {
            'width': '90px',
            '& .MuiInputBase-input': {
              textAlign: 'end',
            },
          },
          '& .slider-input.flat': {
            '& .MuiOutlinedInput-root fieldset': {
              border: 'none',
            },
            '& .MuiOutlinedInput-root.Mui-focused fieldset': {
              border: '2px solid',
              borderColor: theme.palette.primary.main,
            },
            '& .MuiOutlinedInput-root.Mui-error fieldset': {
              border: '1px solid',
              borderColor: theme.palette.error.main,
            },
            '& .MuiOutlinedInput-root.Mui-error.Mui-focused fieldset': {
              border: '2px solid',
              borderColor: theme.palette.error.main,
            },
          },
          '& .menu-box': {
            'display': 'inline-flex',
            '& .MuiIconButton-root': {
              padding: 0,
              marginLeft: '7px',
            },
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: () => ({
          '& .MuiInputBase-input': {
            paddingTop: '5px',
            paddingBottom: '5px',
          },
          '& .select-placeholder': {
            color: Colors.secondaryLight,
          },
        }),
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: () => ({
          '& .MuiSlider-thumb': {
            height: '16px',
            width: '16px',
          },
        }),
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: () => ({
          button: {
            fontSize: '18px',
          },
        }),
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        root: () => ({
          button: {
            fontSize: '18px',
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '& .MuiTabs-scroller': {
            'display': 'flex',
            'justifyContent': 'center',
            '& .MuiTab-root:not(:last-child)': {
              marginRight: ownerState.variant === 'standard' ? '0px' : '40px',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              color: Colors.primaryBase,
              minWidth: '130px',
            },
            '& .Mui-selected': {
              color: Colors.secondaryBase,
              borderColor: Colors.secondaryBase,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: Colors.secondaryBase,
              height: '3px',
            },
          },
          ...(ownerState.className === 'tabs-pale-toggle' && {
            'minHeight': 31,
            'backgroundColor': 'transparent',
            'borderRadius': '4px',
            'padding': '2px',
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTabs-scroller .MuiTab-root': {
              minHeight: 31,
              minWidth: 105,
              borderRadius: '4px',
              padding: '5px 14px',
              backgroundColor: Colors.tertiaryBase,
              color: Colors.secondaryBase,
              fontWeight: 400,
              fontSize: '16px',
            },
            '& .MuiTabs-scroller .MuiTab-root.Mui-selected': {
              backgroundColor: Colors.primaryLighter,
              color: Colors.secondaryBase,
              fontWeight: 600,
            },
            '& .MuiTabs-scroller .MuiTab-root.Mui-selected:hover': {
              backgroundColor: Colors.primaryLight,
            },
            '& .MuiTabs-scroller .MuiTab-root:hover': {
              backgroundColor: Colors.disabledLight,
            },
            '& .MuiTabs-scroller .MuiTab-root:not(:last-child)': {
              marginRight: 0,
            },
          }),
        }),
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: () => ({
          '& .MuiBreadcrumbs-separator': {
            color: Colors.primaryBase,
          },
        }),
      },
    },
    MuiSkeleton: {
      variants: [
        {
          props: { variant: 'rectangular' },
          style: {
            borderRadius: '5px',
          },
        },
      ],
    },
  },
});

const ThemeWrapper: React.FC<React.PropsWithChildren> = (props) => {
  return <ThemeProvider theme={theme}>{props.children}</ThemeProvider>;
};

export default ThemeWrapper;
